/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import ReactDOM from 'react-dom';
import Extent from '@arcgis/core/geometry/Extent';
import Graphic from '@arcgis/core/Graphic';
import Point from 'esri/geometry/Point';
import {cloneDeep} from 'lodash';
import invert from 'lodash.invert';
import isArray from 'lodash.isarray';
import isboolean from 'lodash.isboolean';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import isNumber from 'lodash.isnumber';
import isString from 'lodash.isstring';
import isUndefined from 'lodash.isundefined';
import sortby from 'lodash.sortby';
import unionBy from 'lodash.unionby';
import uniqBy from 'lodash.uniqby';
import {
  getAssetState,
  getLatestTime,
  isIgnitionOff
} from '@ion/core/src/entities/Asset';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {searchMapAddressCandidates, searchMapSuggest} from '@ion/core/src/services/ArcGIS';
import {parseCollection} from '@ion/core/src/services/ArcGISUtils';
import Device from '@ion/core/src/services/Device';
import {
  EventClassification,
  EventClassificationEntriesMap,
  EventCodeValueType,
  getEventCodesByClassification,
  getEventList,
  getEventName
} from '@ion/core/src/services/EventCodes';
import {
  getMapAddressCandidates,
  suggestMapSearch
} from '@ion/core/src/services/Here';
import {
  AssetState,
  BaseAsset,
  Crumb,
  CVSAssetAggregations,
  CVSAssetType,
  GenericObject,
  HistoryEventByAsset,
  HVSRecord,
  LatLong,
  LatLongGroup,
  SCDevicePopupProps,
  SCDeviceType
} from '@ion/core/src/types/entities';
import {isMetric} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {isFalse, isTrue} from '@ion/core/src/utils/BooleanUtils';
import {timeDiff} from '@ion/core/src/utils/DateUtil';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {inRange, isNonZeroNumber} from '@ion/core/src/utils/NumberUtil';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {
  CLUSTER_LAYER_ID,
  DEFAULT_AGGREGATION_LEVEL
} from '@ion/dls/src/maps/layers/AssetLayer';
import {GEOFENCE_LAYER} from '@ion/dls/src/maps/layers/GeofenceLayerSettings';
import {SC_DEVICE_LAYER_ID} from '@ion/dls/src/maps/layers/SCDeviceLayer';
import {TRAFFIC_LAYER} from '@ion/dls/src/maps/layers/TrafficLayerSettings';
import {HISTORY_SEPARATOR} from '@ion/dls/src/maps/layers/TripHistoryLayerSettings';
import {WEATHER_LAYER} from '@ion/dls/src/maps/layers/WeatherLayerSettings';
import {getAssetEventNoGPSMarker} from '@ion/dls/src/maps/markers/AssetEventNoGPSMarker';
import {getAssetEventStatusMarker} from '@ion/dls/src/maps/markers/AssetEventStatusMarker';
import {HistoryDefaultInputsEnum} from '@ion/dls/src/maps/types/types';
import {
  AssetsMap,
  BaseMapEnum,
  BreadcrumbState,
  ColorModeEnum,
  CurrentMapLayer,
  EventLineLayer,
  HistoryInputEvents,
  HistoryInputEventsColorBar,
  HistoryInputEventsEnum,
  HistoryReportTypeEnum,
  LastAssetPosition,
  MapToolsPositionEnum,
  SavedMapViewType
} from '@ion/dls/src/types/types';
import RootManager from '../../client/RootManager';
import {dlsAlt, dlsColor} from '../../theme/theme';
import SCDevicePopup from '../components/SCDevicePopup';
import {
  ASSET_CLUSTER,
  EVENT_TRIANGLE_PATH,
  EXTENT_FACTOR,
  FLEET_ASSET_LAYER_ID,
  HISTORY_EVENT_ICON_BY_ASSET,
  IN_TRIP_PATH_LAYER,
  mapViewGoToOptions,
  MIN_DISTANCE_FOR_DIRECTION_ARROW,
  SymbolTypes
} from '../constants';
import {
  getAssetEventMarker
} from '../markers/AssetEventMarker';
import {
  getAssetEventOffsetEUMarker
} from '../markers/AssetEventOffsetEUMarker';
import {
  getAssetEventOffsetUSMarker
} from '../markers/AssetEventOffsetUSMarker';
import {
  MarkerArrowColor
} from '../markers/AssetMarkers';
import {
  getAssetStartEventMarker
} from '../markers/AssetStartEventMarker';
import {
  getAssetStartEventOffsetEUMarker
} from '../markers/AssetStartEventOffsetEUMarker';
import {
  getAssetStartEventOffsetUSMarker
} from '../markers/AssetStartEventOffsetUSMarker';
import {
  getAssetStopEventMarker
} from '../markers/AssetStopEventMarker';
import {
  getAssetStopEventOffsetEUMarker
} from '../markers/AssetStopEventOffsetEUMarker';
import {
  getAssetStopEventOffsetUSMarker
} from '../markers/AssetStopEventOffsetUSMarker';
import {
  getBatteryEventMarker
} from '../markers/BatteryEventMarker';
import {
  getBatteryEventOffsetEUMarker
} from '../markers/BatteryEventOffsetEUMarker';
import {
  getBatteryEventOffsetUSMarker
} from '../markers/BatteryEventOffsetUSMarker';
import {
  getIgnitionOffEventMarker
} from '../markers/IgnitionOffEventMarker';
import {
  getIgnitionOffEventOffsetEUMarker
} from '../markers/IgnitionOffEventOffsetEUMarker';
import {
  getIgnitionOffEventOffsetUSMarker
} from '../markers/IgnitionOffEventOffsetUSMarker';
import {
  getIgnitionOnEventMarker
} from '../markers/IgnitionOnEventMarker';
import {
  getIgnitionOnEventOffsetEUMarker
} from '../markers/IgnitionOnEventOffsetEUMarker';
import {
  getIgnitionOnEventOffsetUSMarker
} from '../markers/IgnitionOnEventOffsetUSMarker';
import {
  getInputEventMarker
} from '../markers/InputEventMarker';
import {
  getInputEventOffsetEUMarker
} from '../markers/InputEventOffsetEUMarker';
import {
  getInputEventOffsetUSMarker
} from '../markers/InputEventOffsetUSMarker';
import getLocationMarker from '../markers/LocationMarkers';
import {
  getStopEventMarker as getStopMarker
} from '../markers/StopEventMarker';
import {
  getStopEventOffsetEUMarker as getStopOffsetEUMarker
} from '../markers/StopEventOffsetEUMarker';
import {
  getStopEventOffsetUSMarker as getStopOffsetUSMarker
} from '../markers/StopEventOffsetUSMarker';
import {getSearchExtentParams, SavedPlacesProps} from '../search/SearchView';
import PointSymbol from '../symbols/PointSymbol';
import {initializeEsriModules} from './initializeEsriModules';
import {getLabelClassQuery, getLayerLabelClass} from './LabelUtils';

enum ASSET_EVENT {
  MOVING = 'MOVING',
  START = 'START',
  STOP = 'STOP',
  IGNITION_OFF = 'IGNITION_OFF',
  IGNITION_ON = 'IGNITION_ON',
  STOP_LIVE = 'STOP_LIVE',
  BATTERY = 'BATTERY',
  INPUT_EVENT = 'INPUT_EVENT'
};

enum ENVIRONMENT {
  US = 'US',
  UK = 'UK'
};

export enum VERSION {
  V1 = 'V1',
  V2 = 'V2'
};

type AssetEnv = ENVIRONMENT;
type AssetEvent = ASSET_EVENT;
type AssetMarkerVersion = VERSION;

const V1_MARKERS = {
  [ASSET_EVENT.MOVING]       : getAssetEventMarker,
  [ASSET_EVENT.START]        : getAssetStartEventMarker,
  [ASSET_EVENT.STOP]         : getAssetStopEventMarker,
  [ASSET_EVENT.IGNITION_ON]  : getIgnitionOnEventMarker,
  [ASSET_EVENT.IGNITION_OFF] : getIgnitionOffEventMarker,
  [ASSET_EVENT.STOP_LIVE]    : getStopMarker,
  [ASSET_EVENT.BATTERY]      : getBatteryEventMarker,
  [ASSET_EVENT.INPUT_EVENT]  : getInputEventMarker
};

const V2_MARKERS = {
  [ENVIRONMENT.US] : {
    [ASSET_EVENT.MOVING]       : getAssetEventOffsetUSMarker,
    [ASSET_EVENT.START]        : getAssetStartEventOffsetUSMarker,
    [ASSET_EVENT.STOP]         : getAssetStopEventOffsetUSMarker,
    [ASSET_EVENT.IGNITION_ON]  : getIgnitionOnEventOffsetUSMarker,
    [ASSET_EVENT.IGNITION_OFF] : getIgnitionOffEventOffsetUSMarker,
    [ASSET_EVENT.STOP_LIVE]    : getStopOffsetUSMarker,
    [ASSET_EVENT.BATTERY]      : getBatteryEventOffsetUSMarker,
    [ASSET_EVENT.INPUT_EVENT]  : getInputEventOffsetUSMarker
  },
  [ENVIRONMENT.UK] : {
    [ASSET_EVENT.MOVING]       : getAssetEventOffsetEUMarker,
    [ASSET_EVENT.START]        : getAssetStartEventOffsetEUMarker,
    [ASSET_EVENT.STOP]         : getAssetStopEventOffsetEUMarker,
    [ASSET_EVENT.IGNITION_ON]  : getIgnitionOnEventOffsetEUMarker,
    [ASSET_EVENT.IGNITION_OFF] : getIgnitionOffEventOffsetEUMarker,
    [ASSET_EVENT.STOP_LIVE]    : getStopOffsetEUMarker,
    [ASSET_EVENT.BATTERY]      : getBatteryEventOffsetEUMarker,
    [ASSET_EVENT.INPUT_EVENT]  : getInputEventOffsetEUMarker
  }
};

const ICON_OFFSET_SIZE = {
  small      : 23,
  large      : 30,
  harshEvent : 35,
  ignition   : 31,
  stopLive   : 31,
  battery    : 34,
  inputEvent : 34,
  start      : {width: 35, height: 33},
  stop       : {width: 35, height: 36}
};

type GraphicMap = {
  fillColor: any,
  isAssetHistory: boolean,
  iconSize: ASSET_HISTORY_ICON_SIZE,
  offset: boolean,
  env?: ENVIRONMENT
};

type HistoryByAssetRenderer = {
  assets: HVSRecord[],
  reportType: HistoryReportTypeEnum,
  iconSize?: number,
  offset?: boolean,
  env?: ENVIRONMENT
};

type CreateUniqueValue = {
  assets: HVSRecord[],
  reportType: HistoryReportTypeEnum,
  iconSize: number,
  offset: boolean,
  env?: ENVIRONMENT
};

/**
 * Enum representing the vertical position (in pixels) of a general map widget
 * based on whether a filter is applied or not
 */
export enum MAP_WIDGET_POSITION {
  topWithOutFilter = 89, // Position when the filter is not applied
  topWithFilter = 134 // Position when the filter is applied
};

/**
 * Enum representing the vertical position (in pixels) of a geofence map widget
 * based on whether a filter is applied or not
 */
export enum MAP_GEOFENCE_WIDGET_POSITION {
  topWithOutFilter = 185, // Position when the filter is not applied
  topWithFilter = 230 // Position when the filter is applied
};

// Defines View's zoom level constraints
enum ViewMinZoomLevel {
  phone = 0,
  desktop = 2
};

/**
 * @private
 */
const MIN_PERCENT = 25;

/**
 * @private
 */
const MAX_PERCENT = 100;

// Default Layer Index
const DEFAULT_LAYER_INDEX = -1;

// Define a constant for a count of single Asset
export const ASSET_SINGLE_COUNT = 1;

// Default value for none selected item
export const noneSelectedQuery = '1 = 0';

// Default Icon Size for History Reports
enum ASSET_HISTORY_ICON_SIZE {
  defaultSize = 15.5,
  smallSize = 7.5
};

enum IconSize {
  Normal = 'Normal',
  Medium = 'Medium',
  Large = 'Large'
};

export interface RendererMapType {
  [IconSize.Normal]: __esri.Renderer;
  [IconSize.Medium]: __esri.Renderer;
  [IconSize.Large]: __esri.Renderer;
};

export const IconZoomThreshold = {
  [IconSize.Normal] : 13,
  [IconSize.Large]  : 15
};

const IconSizeMultiplier = {
  [IconSize.Normal] : 1.0,
  [IconSize.Medium] : 1.40,
  [IconSize.Large]  : 1.80
};

// the color list position an asset keeps for as long as a report runs - so
// its color does not move as other assets are added. Without it, an asset's
// color is read off its position among the assets given ...
export type AssetColorIndexOf = (assetKey: string) => number | undefined;

type CreateNodeRendererProps = {
  inTrip: boolean;
  assetsMap: any;
  isAssetHistory?: boolean;
  iconSize?: number;
  offset?: boolean;
  env?: ENVIRONMENT;
  colorIndexOf?: AssetColorIndexOf;
};

type CreateUniqNodeRendererProps = {
  inTrip: boolean;
  assetsMap: any;
  isAssetHistory?: boolean;
  iconSize?: number;
  offset?: boolean;
  env?: ENVIRONMENT;
  colorIndexOf?: AssetColorIndexOf;
};

// the color an asset is drawn in - at the position it keeps where one is
// given, otherwise at its position among the assets drawn ...
type AssetColorArgs = {
  colorList: string[];
  assetKey: string;
  position: number;
  colorIndexOf?: AssetColorIndexOf;
};

const getAssetColor = ({colorList, assetKey, position, colorIndexOf}: AssetColorArgs): string => {
  const index = colorIndexOf?.(assetKey) ?? position;
  return colorList[index % colorList.length] || dlsColor('skyBlue');
};

// how the end-of-trip node renderer is drawn - offset for the larger icons,
// and the color position each asset keeps, where one is given ...
export type EndNodeRendererOptions = {
  offset?: boolean;
  colorIndexOf?: AssetColorIndexOf;
};

// defines the prop definitions for the MapChildren
interface MapChildrenParams {
  [elemName: string]: any;
  components: any;
}

interface LatestUpdateParams {
  list: CVSAssetType[],
  item: CVSAssetType,
  key?: string,
  prop?: string
}

export interface Coordinates {
  x: number;
  y: number;
}

export type Layer = {
  id: number | string,
  opacity: number,
  title: string,
  url: string,
  layerType: string
  layers: Sublayer[],
  visibility: boolean
};

export type Sublayer = {
  id: number,
  name: string,
  parentLayerId: number
  defaultVisibility: boolean
};

export type AddComponentParams = {
  component: JSX.Element,
  position: MapToolsPositionEnum,
  id: string,
  view: __esri.MapView,
  usePortal?: boolean,
  index?: number,
  // re-renders a component already on the map in place, rather than adding
  // it to the map's UI again - which moves it in the DOM, resetting e.g.
  // its scroll position ...
  keepPlacement?: boolean
};

export type HitTestParms = {
  view: __esri.MapView,
  layerView?: __esri.LayerView,
  options?: GenericObject,
  layerId?: string | string[],
  handler: Function,
  zoomHandler?: Function,
  includeAny?: boolean,
  includeResults?: boolean,
  hitTestVersion?: VERSION
}

export type SublayerTree = {
  id: number,
  parentLayerId?: number,
  children?: object[]
}

type LayerEffectQueryParams = {
  selectedItem: any,
  type: string,
  query: string,
  defaultQuery?: string
}

type ItemQueryParams = {
  item: CVSAssetType | SCDeviceType,
  property: string,
  defaultQuery: any
}

type IndividualFeature = {
  handler: Function,
  results: any[]
};

type ZoomToClusterExtent = {
  view: __esri.MapView,
  layerView: __esri.FeatureLayerView,
  graphic: __esri.Graphic
};

export interface CircleMarkerParams {
  size?: number;
  width?: number;
  fillColor?: string;
  outlineColor?: string;
  offset?: boolean;
}

export interface TriangleMarkerParams extends CircleMarkerParams {
  angle?: number;
}

export interface SimpleLineParams {
  color?: string;
  width?: number;
}

export interface PolyLineParams {
  paths: Array<Array<string>>
}

export interface PointParams {
  longitude: number;
  latitude: number;
}

export interface GetGraphicParams {
  item: HVSRecord;
  index: number;
  event?: HVSRecord
  tripView?: boolean;
  firstEventIndex?: number;
  lastEventIndex?: number;
  isStartOfTrip?: boolean;
}

export interface GetMapPointsParams {
  mapView: __esri.MapView;
  newLatitude?: null | number;
  newLongitude?: null | number;
  mapPointLongitude: number;
  mapPointLatitude: number;
};

export interface GetTrackingAssets {
  assetsNormalFleet: Device[];
  assetsLargeFleet: CVSAssetAggregations[];
  viewZoom: number;
  isLargeFleet: boolean
};

export interface LayerClickEvent {
  x: number,
  y: number,
  hitTestVersion?: VERSION,
  includeAny: boolean,
  includeResults: boolean,
  view: __esri.MapView,
  layerId: string[],
  callback: Function
};

export interface GetLocationsParams {
  searchText: string;
  mapView: __esri.MapView;
  cancelToken: any;
  useExtent: boolean;
  useHereApi?: boolean;
};

export interface AssetEventMarker {
  fillColor?: string,
  arrowColor?: string,
  size?: number,
  width?: number,
  height?: number,
  angle?: number,
  opacity?: number,
  env?: AssetEnv,
  version?: AssetMarkerVersion
};

// defines the dom containers and their associated root apis ...
const roots = new RootManager();

// defines a reference to the pre-loaded graphics modules ...
let graphicsModules;

export const assetIdField = 'assetId';

const getAssetMarkerVersion = (isV2: boolean): AssetMarkerVersion =>
  isV2 ? VERSION.V2 : VERSION.V1;

/**
 * @MapChildren
 *
 * Provides a utility which iterates over a collection of Map components arrays,
 * such as an array of layers and an array of widgets, and adds all items as
 * children of a DLSMap instance, passing down the provided props to each child
 * component within the map.
 *
 * ```typescript
 *    import React from 'react';
 *    import {MapChildren} from '@ion/dls/src/maps/helpers';
 *
 *    const DLSMap = ({layers, widgets, ...props}) => {
 *      const components = [layers, widgets];
 *      return (
 *        <Map mapProperties={{basemap}}>
 *          <MapChildren components={components} props={props} />
 *        </Map>
 *      );
 *    }
 *
 * ```
 */
export const MapChildren = ({components = [], ...props}: MapChildrenParams) => {
  return components?.map((Comp, i) => <Comp key={i} {...props} />) || null;
};

/**
 * @addComponent
 *
 * Provides a utility which allows for adding a new component to a DLSMap
 * instance.
 *
 * ```typescript
 *    import React from 'react';
 *    import {addComponent} from '@ion/dls/src/maps/helpers';
 *
 *    const component = (
 *      <ZoomInOutTool onZoomOut={function() {
 *        const {center, zoom} = view;
 *        view.goTo({zoom: zoom - 1, center});
 *      }} onZoomIn={function() {
 *        const {center, zoom} = view;
 *        view.goTo({zoom: zoom + 1, center});
 *      }} />
 *    );
 *    addComponent({
 *      id        : 'map-zoom-in-out',
 *      position  : 'bottom-right'
 *      component,
 *      view
 *     });
 *
 * ```
 */
export const addComponent = ({
  component,
  position,
  id,
  view,
  usePortal,
  index = 1,
  keepPlacement = false
}: AddComponentParams) => {
  const container = RootManager.createContainer(id);
  const isPlaced  = keepPlacement && Boolean((view?.container as HTMLElement)?.contains?.(container));
  if (usePortal) {
    ReactDOM.createPortal(component, container);
  } else {
    roots.render(component, container);
  }
  if (!isPlaced) {
    view.ui.add(container, {
      position,
      index
    });
  }
  return component;
};

/**
 * Provides a utility which removes a component added with `addComponent`
 * from a DLSMap instance and unmounts it (unlike `removeComponent`, which
 * only takes it off the map's UI) - the widget leaves the map's UI straight away and
 * its React root is unmounted on the next tick (a root cannot be unmounted
 * while React is still rendering, e.g. from an effect cleanup).
 *
 * ```typescript
 *
 *   import {unmountComponent} from '@ion/dls/src/maps/helpers/MapViewUtils';
 *   ...
 *
 *   unmountComponent({id: 'map-bottom-right-tools', view});
 *
 * ```
 *
 * @param   params The component's `id` and the `view` it was added to.
 * @returns        `true` when the component was found and removed,
 *                 otherwise `false`.
 */
export const unmountComponent = ({id, view}: {id: string, view: __esri.MapView}): boolean => {
  const container = RootManager.getContainer(id);
  if (container) {
    view?.ui?.remove(container);
    const timeout = setTimeout(function() {
      clearTimeout(timeout);
      roots.unmount(container);
    }, 0);
  }
  return !!container;
};

/**
 * Clears all UI components from a given ArcGIS MapView instance,
 * including both custom and default widgets. This utility is useful
 * for resetting the map UI to a clean state, such as during layer
 * switches, view resets, or teardown operations.
 *
 * ```typescript
 * import {removeAllUIComponents} from '@ion/dls/src/maps/helpers';
 *
 * const view = getMapViewSomehow(); // __esri.MapView instance
 * removeAllUIComponents(view);
 * ```
 *
 * @param view - The ArcGIS MapView from which all UI elements should be removed.
 */
export const removeAllUIComponents = (view: __esri.MapView) => {
  if (view?.ui) {
    // Remove all manually added widgets
    for (const position of ['top-left', 'top-right']) {
      view.ui.empty(position as __esri.UIPosition);
    }

    // Close popup and reset selected features
    view.popup?.close?.();
    view.graphics?.removeAll?.();
  }
};

/**
 * Given a direction in degrees, converts the value to it's cardinal
 * equivalent.
 *
 * @method toCardinal
 *
 * ```typescript
 *  import {toCardinal} from '@ion/dls/src/maps/helpers/MapViewUtils';
 *
 *  toCardinal(item);
 *
 * ```
 */
export const toCardinal = (item: number) => {
  const directions = 8;
  const degree = 360 / directions;
  const angle  = item + degree / 2;
  const expressions = {
    N  : angle >= 0 * degree && angle < 1 * degree,
    NE : angle >= 1 * degree && angle < 2 * degree,
    E  : angle >= 2 * degree && angle < 3 * degree,
    SE : angle >= 3 * degree && angle < 4 * degree,
    S  : angle >= 4 * degree && angle < 5 * degree,
    SW : angle >= 5 * degree && angle < 6 * degree,
    W  : angle >= 6 * degree && angle < 7 * degree,
    NW : angle >= 7 * degree && angle < 8 * degree
  };
  return invert(expressions).true || 'N';
};

/**
 * @removeComponent
 *
 * Provides a utility which allows for adding any exiting custom component by id
 *
 * ```typescript
 *    import React from 'react';
 *    import {removeComponent} from '@ion/dls/src/maps/helpers';
 *
 *    removeComponent('map-layer-chooser', view);
 *
 * ```
 */
export const removeComponent = (id: string, view: __esri.MapView) => {
  view.ui.remove(id);
};

/**
 * Predefined offsets (in pixels) around a click point to improve hit test accuracy.
 * Used to try neighboring positions in case the user clicks slightly off a graphic.
 */
const CLICK_EVENT_OFFSETS = [
  {dx: 0, dy: 0},
  {dx: -12, dy: 0},
  {dx: 12, dy: 0},
  {dx: 0, dy: -12},
  {dx: 0, dy: 12}
];

/**
 * Calculates the Euclidean distance between two screen points.
 * @param a - First point with x and y.
 * @param b - Second point with x and y.
 * @returns Distance in pixels.
 */
const distance = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Picks the closest graphic from a list of hit test results to a given click position.
 * @param results - Array of hit test results containing graphics.
 * @param view - Map view used to convert geometry to screen coordinates.
 * @param click - Original click coordinates.
 * @returns The graphic closest to the click or null if none found.
 */
const pickClosestGraphic = (
  results,
  view,
  click
) => {
  let closest = null;
  let minDist = Infinity;

  for (const {graphic} of results) {
    if (!graphic?.geometry) continue;

    const screenPoint = view.toScreen(graphic.geometry);
    if (!screenPoint) continue;

    const d = distance(screenPoint, click);

    if (d < minDist) {
      minDist = d;
      closest = graphic;
    }
  }

  return closest;
};

/**
 * Performs a hit test on a map view to detect point graphics near a given screen coordinate.
 *
 * Behavior:
 * 1. Attempts hit testing at multiple offsets around the click position (center and ±12px in X/Y)
 *    to increase the chance of selecting nearby point graphics.
 * 2. Filters results to only include point geometries from the specified layer(s).
 * 3. Picks the closest graphic to the original click position if multiple matches are found.
 * 4. Calls the provided handler with the selected graphic's attributes, the graphic itself, and the
 *    tested coordinates.
 * 5. If no matching graphic is found and `includeAny` is true, performs a hit test at the original
 *    click location and calls the handler with either all results or null depending on `includeResults`.
 *
 * @param {Coordinates} {x, y} - The screen coordinates where the hit test originates.
 * @param {HitTestParms} - Options including:
 *   - view: The map view to perform the hit test on.
 *   - layerId: The layer ID(s) to filter graphics by.
 *   - handler: Callback invoked when a matching graphic is found.
 *   - includeAny: If true, calls handler even if no matching graphics are found.
 *   - includeResults: If true, passes all hit test results to the handler when no matching graphic is found.
 * 
 * ```typescript
 *  view.on('click', function({x, y}) {
 *    hitTest({x, y}, {
 *      view,
 *      layerId : 'some-layer',
 *      handler : (attributes, graphic, coordinates}) => {
 *       ...
 *      }
 *    });
 *  });
 * ```
 */
export const hitTestV2 = (
  {x, y}: Coordinates,
  {
    view,
    layerId,
    handler,
    includeAny = false,
    includeResults = false
  }: HitTestParms
) => {
  const run = async() => {
    for (const {dx, dy} of CLICK_EVENT_OFFSETS) {
      const coords = {x: x + dx, y: y + dy};
      const {results} = await view.hitTest(coords);
      const selected = results?.filter(({graphic}) => {
        const id = graphic?.layer?.id;
        return (
          graphic?.geometry?.type === 'point' &&
          Array.isArray(layerId)
            ? layerId.includes(id)
            : id === layerId
        );
      });

      if (selected?.length) {
        const graphic = pickClosestGraphic(selected, view, {x, y});
        handler(graphic.attributes, graphic, coords);
        return;
      }
    }

    if (includeAny) {
      const {results} = await view.hitTest({x, y});
      handler(includeResults ? results : null);
    }
  };

  run();
};

/**
 * @function hitTest
 *
 * Provides the hitTest implementation for all map gestures, invoking the given
 * handler when the event occurs on an item within the layer of interest.
 *
 * ```typescript
 *   const onSelect = (view, layerId, handler) => {
 *     view.on('click', function({x, y}) {
 *       hitTest({x, y}, {view, layerId, handler});
 *     });
 *   };
 *
 *   view.on('click', function({x, y}) {
 *     hitTest({x, y}, {
 *        view,
 *        layerId : 'some-layer',
 *        handler : (attributes, graphic, coordinates}) => {
 *         ...
 *        }
 *      });
 *   });
 *
 * ```
 */
export const hitTest = ({x, y}: Coordinates, {
  view,
  layerId,
  handler,
  includeAny = false,
  includeResults = false
}: HitTestParms) => {
  const coordinates = {x, y};
  view.hitTest(coordinates).then(({results}) => {
    const selected = results?.filter(({graphic}) => {
      const {id} = graphic?.layer || {};
      return isArray(layerId) ? layerId?.includes(id) : id === layerId;
    });
    if (selected?.length) {
      const graphic = selected[0].graphic;
      const {attributes} = graphic;
      handler(attributes, graphic, coordinates);
    } else if (includeAny) {
      handler(includeResults ? results : null);
    }
  });
};

/**
 * @function onSelect
 *
 * Provides a utility which allows for invoking a callback when an item within
 * the given layer is clicked, which, when true, invokes the provided handler
 * with a reference to the item (attributes), the graphic, and the coordinates
 * of the selected item.
 *
 * ```typescript
 *
 *   useEffect(() => {
 *      loadModules(['esri/layers/GraphicsLayer', 'esri/Graphic'])
 *       .then(([GraphicsLayer, Graphic]) => {
 *         const layer = new GraphicsLayer({
 *         graphics : markers.generate(assets, Graphic),
 *         id       : 'assets',
 *       });
 *       map.add(layer);
 *       onSelect(view, 'assets', onSelectedAsset);
 *     }).catch(err => {
 *       console.error(err);
 *     });
 *     return function() {
 *       view.graphics.removeAll();
 *     };
 *   }, [view]);
 *
 * ```
 *
 */
export const onSelect = ({
  view,
  layerId,
  handler,
  includeAny = false,
  hitTestVersion = VERSION.V1
}: HitTestParms) => {
  const method = VIEW_HIT_TEST_METHOD[hitTestVersion];
  view.on('click', ({x, y}) => {
    method({x, y}, {view, layerId, handler, includeAny});
  });
};

/**
 * @function onHover
 *
 * Provides a utility which allows for invoking a callback when an item within
 * the given layer is clicked, which, when true, invokes the provided handler
 * with a reference to the item (attributes), the graphic, and the coordinates
 * of the selected item.
 *
 * ```typescript
 *
 *   useEffect(() => {
 *      loadModules(['esri/layers/GraphicsLayer', 'esri/Graphic'])
 *       .then(([GraphicsLayer, Graphic]) => {
 *         const layer = new GraphicsLayer({
 *         graphics : markers.generate(assets, Graphic),
 *         id       : 'assets',
 *       });
 *       map.add(layer);
 *       onSelect(view, 'assets', onSelectedAsset);
 *     }).catch(err => {
 *       console.error(err);
 *     });
 *     return function() {
 *       view.graphics.removeAll();
 *     };
 *   }, [view]);
 *
 * ```
 *
 */
export const onHover = ({
  view,
  layerId,
  handler,
  includeAny = false
}: HitTestParms) => {
  view.on('pointer-move', function({x, y}) {
    hitTest({x, y}, {view, layerId, handler, includeAny});
  });
};

/**
 * @function getLayerIndex
 *
 * Provides a utility which allows for getting the selected layer's order when adding
 * to map.
 *
 * ```typescript
 *    import React from 'react';
 *    import {getLayerIndex} from '@ion/dls/src/maps/helpers';
 *
 *    getLayerIndex([layers array], currentLayer);
 *
 * ```
 */
export const getLayerIndex = (map: __esri.Map | {layers: {get: Function}}, currentLayer: string) => {
  const layers: any = map?.layers?.get('items');
  let index         = layers.length - 1;

  const defaultIndexes = {
    assetLayerIndex    : DEFAULT_LAYER_INDEX,
    fleetLayerIndex    : DEFAULT_LAYER_INDEX,
    deviceLayerIndex   : DEFAULT_LAYER_INDEX,
    geofenceLayerIndex : DEFAULT_LAYER_INDEX,
    trafficLayerIndex  : DEFAULT_LAYER_INDEX,
    weatherLayerIndex  : DEFAULT_LAYER_INDEX
  };

  const layerIndexes = getCurrentLayerIndex(layers, defaultIndexes);

  switch (currentLayer) {
    case SC_DEVICE_LAYER_ID:
      index = getDeviceLayerIndex(layerIndexes, index, DEFAULT_LAYER_INDEX);
      break;
    case GEOFENCE_LAYER:
      index = getGeofenceLayerIndex(layerIndexes, index, DEFAULT_LAYER_INDEX);
      break;
    case TRAFFIC_LAYER:
      index = getTrafficLayerIndex(layerIndexes, index, DEFAULT_LAYER_INDEX);
      break;
    case WEATHER_LAYER:
      index = getWeatherLayerIndex(layerIndexes, index, DEFAULT_LAYER_INDEX);
      break;
    default:
      break;
  };
  return index;
};

// Defines a utility to get the index of current layers
const getCurrentLayerIndex = (layers: __esri.Collection<__esri.Layer>, layerIndexes: any) => {
  layers?.map(({id}, index) => {
    switch (id) {
      case FLEET_ASSET_LAYER_ID:
        layerIndexes.fleetLayerIndex = index;
        break;
      case CLUSTER_LAYER_ID:
        layerIndexes.assetLayerIndex = index;
        break;
      case GEOFENCE_LAYER:
        layerIndexes.geofenceLayerIndex = index;
        break;
      case TRAFFIC_LAYER:
        layerIndexes.trafficLayerIndex = index;
        break;
      case WEATHER_LAYER:
        layerIndexes.weatherLayerIndex = index;
        break;
      case SC_DEVICE_LAYER_ID:
        layerIndexes.deviceLayerIndex = index;
        break;
    };
  });
  return layerIndexes;
};

// Defines a utility to return the index for the Device Layer based on some conditions
const getDeviceLayerIndex = (layerIndexes: any, index: number, defaultIndex: number) => {
  const {assetLayerIndex} = layerIndexes || null;
  if (returnLayerIndex(assetLayerIndex, defaultIndex)) {
    return assetLayerIndex;
  }
  return index;
};

// Defines a utility to return the index for the Geofence Layer based on some conditions
const getGeofenceLayerIndex = (layerIndexes: any, index: number, defaultIndex: number) => {
  const {deviceLayerIndex, assetLayerIndex, fleetLayerIndex} = layerIndexes || null;
  if (returnLayerIndex(deviceLayerIndex, defaultIndex)) {
    return deviceLayerIndex;
  }
  if (returnLayerIndex(assetLayerIndex, defaultIndex)) {
    return assetLayerIndex;
  }
  if (returnLayerIndex(fleetLayerIndex, defaultIndex)) {
    return fleetLayerIndex;
  }
  return index;
};

// Defines a utility to return the index for the Traffic Layer based on some conditions
const getTrafficLayerIndex = (layerIndexes: any, index: number, defaultIndex: number) => {
  const {geofenceLayerIndex, deviceLayerIndex, assetLayerIndex} = layerIndexes || null;
  if (returnLayerIndex(geofenceLayerIndex, defaultIndex)) {
    return geofenceLayerIndex;
  }
  if (returnLayerIndex(deviceLayerIndex, defaultIndex)) {
    return deviceLayerIndex;
  }
  if (returnLayerIndex(assetLayerIndex, defaultIndex)) {
    return assetLayerIndex;
  }
  return index;
};

// Defines a utility to return the index of the Weather Layer based on some conditions
const getWeatherLayerIndex = (layerIndexes: any, index: number, defaultIndex: number) => {
  const {
    trafficLayerIndex,
    geofenceLayerIndex,
    deviceLayerIndex,
    assetLayerIndex
  } = layerIndexes || null;

  if (returnLayerIndex(trafficLayerIndex, defaultIndex)) {
    return trafficLayerIndex;
  }
  if (returnLayerIndex(geofenceLayerIndex, defaultIndex)) {
    return geofenceLayerIndex;
  }
  if (returnLayerIndex(deviceLayerIndex, defaultIndex)) {
    return deviceLayerIndex;
  }
  if (returnLayerIndex(assetLayerIndex, defaultIndex)) {
    return assetLayerIndex;
  }
  return index;
};

// Defines a utility to validate layer index based on
const returnLayerIndex = (layerIndex: number, index: number) => {
  if (isNumber(layerIndex) && isNumber(index)) {
    return !isSameLayerIndex(layerIndex, index);
  }
  return false;
};

// Defines a utility to verify index of the layer bases on a parameter
const isSameLayerIndex = (layerIndex: number, index: number) => {
  return layerIndex === index;
};

/**
 * @function removeActiveFence
 *
 * Provides a utility which removes the geofence and sketch layers to
 * remove a fence that has not yet been saved to the feature layer
 *
 * ```typescript
 *    import {removeActiveFence} from '@ion/dls/src/maps/helpers';
 *
 *    removeActiveFence(map, [geoFencesGraphicsLayerRef, sketchViewModelRef]);
 *
 * ```
 */
export const removeActiveFence = (
  map: __esri.Map | {remove: (ref: any) => void; layers: string[];},
  refs: {current: any}[]
) => {
  refs.forEach(ref => {
    map.remove(ref.current);
    ref.current = null;
  });
};

/**
 * Open new tab and navigate to Google direction by given latitude and longitude.
 *
 * ```typescript
 *   import {getSublayerTree} from '@ion/dls/src/maps/helpers';
 *
 *   const sublayerTree = getSublayerTree(layers, true);
 * ```
 */
export const getSublayerTree = (
  sublayers: Array<Object>,
  includeRoot = false,
  includeLevel = false
): SublayerTree => {
  let tree     = null;
  const level  = 0;
  const root   = {id: -1, parentLayerId: null, children: []};
  const data   = includeRoot ? [root] : [];
  sublayers.forEach(item => data.push({...item, children: []} as any));
  const idMapping = data?.reduce((acc, el, i) => {
    acc[el.id] = i;
    return acc;
  }, {});

  data?.forEach(el => {
    // Handle the root element
    if (el?.parentLayerId === null) {
      tree = el;
      return;
    }
    // Use our mapping to locate the parent element in our data array
    const parentEl = data[idMapping[el?.parentLayerId]];
    // Add our current el to its parent's `children` array
    parentEl.children = [...(parentEl?.children || []), el];
  });

  if (includeLevel) {
    const addChildLevel = (tree: any[], level: number) => {
      tree.forEach((el: { level: number; children: any; }) => {
        el.level = level;
        if (el.children) {
          addChildLevel(el.children, level + 1);
        }
      });
    };
    addChildLevel(tree.children, level);
  }
  return tree;
};

/**
 * Provides a utility which return AVL last position by asset
 *
 * ```typescript
 *    import {getLastPosition} from '@ion/dls/src/maps/helpers';
 *
 *    const lastEvent = getLastPosition(avl);
 *
 * ```
 */
export const getLastPosition = (
  assets: CVSAssetType[],
  reportType: string,
  historyInputEvents: HistoryInputEvents[]
): CVSAssetType[] => {
  const updated      = [];
  const uniqueAssets = uniqBy(assets, assetIdField);
  uniqueAssets.forEach(({assetId}) => {
    let item = null;
    if (reportType === HistoryReportTypeEnum.Event) {
      const features    = getSortedEvents(assets, assetIdField, assetId);
      const records     = features?.map(feature => ({
        ...feature,
        reportType : createReportTypeField(feature, historyInputEvents)
      })) || [];
      const mixedEvents = historyInputEvents?.filter(({label, isSelected}) => label === HistoryInputEventsEnum.Mixed &&
                          isSelected);
      let inputEvents   = historyInputEvents?.filter(({isSelected}) => isSelected)
        .map(({label}) => label);

      if (mixedEvents.length) {
        const mixed = getMixedInputEvents(mixedEvents[0]);
        if (isArray(mixed)) {
          inputEvents = [...inputEvents, ...mixed];
        }
      }

      item = getLatestRecordByEvent({
        assets : records,
        value  : assetId,
        sort   : 'eventTime',
        inputs : inputEvents as any[]
      });
    }
    if (reportType === HistoryReportTypeEnum.Asset) {
      item = getLatestRecordByField(assets, assetId, 'eventTime');
    }
    if (item) {
      updated.push({
        ...item,
        geometry : {
          type        : 'Point',
          coordinates : [item.longitude, item.latitude]
        }
      });
    }
  });
  return updated;
};

/**
 * Defines a utility which returns latest record from array of AVL events based on field
 *
 * ```typescript
 *    import {getLatestRecordByField} from '@ion/dls/src/maps/helpers';
 *
 *    const lastEvent = getLatestRecordByField(assets, assetId, 'eventTime');
 *
 * ```
 */
export const getLatestRecordByField = (assets: CVSAssetType[], field: number, sort: string): CVSAssetType => {
  return sortby(assets?.filter(e => e?.assetId === field), [sort]).reverse()?.[0];
};

/**
 * Defines a utility which returns latest record from array of AVL events based on field
 *
 * ```typescript
 *    import {getLatestRecordByEvent} from '@ion/dls/src/maps/helpers';
 *
 *    const params = {
 *      assets : [],
 *      value  : 'assetId',
 *      types  : ['Plow', 'Spreader']
 *      omit   : 'none',
 *      sort   : 'EventTime'
 *    };
 *
 *    const lastEvent = getLatestRecordByEvent(params);
 *
 * ```
 */
export const getLatestRecordByEvent = ({
  assets,
  value,
  inputs,
  sort
}: LastAssetPosition): CVSAssetType => {
  return sortby(assets?.filter(({assetId, reportType}) => {
    return assetId === value && inputs?.includes(reportType);
  }), [sort]).reverse()?.[0];
};

/* @function isLargeFleetMode
 *
 * Provides a utility which returns if it is Large Fleet Mode based on
 * asset count and the normalFleetMaxAssets parameter in the environment.json
 *
 * @param {number} assetCount asset count
 *
 * ```typescript
 *      import {isLargeFleetMode} from '@ion/dls/src/maps/helpers';
 *
 *      const isLargeFleetMode = isLargeFleetMode(assetCount);
 *
 * ```
 */
export const getIsLargeFleetMode = (assetCount: number): boolean => {
  return assetCount > parseInt(get('normalFleetMaxAssets'));
};

/**
 * @function getLargeFleetParams
 *
 * Provides a utility which returns the Large Fleet Parameters
 * based on the given view
 *
 * @param {__esri.View} view esri View
 *
 * ```typescript
 *      import {getLargeFleetParams} from '@ion/dls/src/maps/helpers';
 *
 *      const params = getLargeFleetParams(view);
 *
 * ```
 */
export const getLargeFleetParams = (view: __esri.MapView, clustetRadius: number, zoomLevel: number): object => {
  const mapUnitsPerPixel = view?.extent?.width / view?.width;
  return {
    groups                  : null,
    assets                  : [],
    query                   : '',
    geometry                : mapUnitsPerPixel < 2445 ? view?.extent : null,
    clusterDistanceInPixels : view.zoom > zoomLevel ? 0 : clustetRadius,
    mapUnitsPerPixel
  };
};

/**
 * @getPercent
 *
 * Provides a utility which returns a porcentage of
 * given parameters
 *
 * @param   {number} a is the number to take percentage of
 * @param   {number} b is the number to get a percentage with respect to A
 *
 * ```typescript
 *      import {getPercent} from '@ion/dls/src/maps/helpers';
 *
 *      const percent = getPercent(a, b);
 *
 * ```
 */
export const getPercent = (a: number, b: number): number => {
  if (isNumber(a) && isNumber(b)) {
    return Math.round((b/a) * 100);
  }
  return 0;
};

/**
  * @isExtentSmallerThanMap
  *
  * Given Point percentage Width and Height returns true if percentage Width and Height are less than 25%,
  * otherwise returns false
  *
  * @param   {number} width is the extent width
  * @param   {number} height is the extent height
  *
  * ```typescript
  *      import {isExtentSmallerThanMap} from '@ion/dls/src/maps/helpers';
  *
  *      const isSmaller = isExtentSmallerThanMap(a, b);
  *
  * ```
  */
export const isExtentSmallerThanMap = (width: number, height: number): boolean => {
  return width < MIN_PERCENT && height < MIN_PERCENT;
};

/**
  * @isExtentBiggerThanMap
  *
  * Given Point percentage Width and Height returns true if percentage Width or Height
  * are greater than 100%, otherwise returns false
  *
  * @param   {number} width is the extent width
  * @param   {number} height is the extent height
  *
  * ```typescript
  *      import {isExtentBiggerThanMap} from '@ion/dls/src/maps/helpers';
  *
  *      const isBigger = isExtentBiggerThanMap(a, b);
  *
  * ```
  */
export const isExtentBiggerThanMap = (width: number, height: number): boolean => {
  return width > MAX_PERCENT || height > MAX_PERCENT;
};

/**
  * @isExtentFitsInMap
  *
  * Given Point percentage Width and Height returns true if percentage Width or Height
  * are greater than 24% and both are equal to / less than 100%, otherwise returns false
  *
  * @param   {number} width is the extent width
  * @param   {number} height is the extent height
  *
  * ```typescript
  *      import {isExtentFitsInMap} from '@ion/dls/src/maps/helpers';
  *
  *      const isFits = isExtentFitsInMap(a, b);
  *
  * ```
  */
export const isExtentFitsInMap = (width: number, height: number): boolean => {
  return (width >= MIN_PERCENT || height >= MIN_PERCENT) && (width <= MAX_PERCENT && height <= MAX_PERCENT);
};

/**
  * @shouldZoomTo
  *
  * isAggregateCluster method validates the cluster extent based on the map extent,
  * returns true is the cluster extent is bigger than / fits in the Map extent in Huge Fleet and Aggregate Levels,
  * otherwise, returns false
  *
  * @param   {__esri.Extent} extent is the extent to validate
  * @param   {__esri.MapView} view is the current MapView
  *
  * ```typescript
  *      import {shouldZoomTo} from '@ion/dls/src/maps/helpers';
  *
  *      const zooming = shouldZoomTo(extent, view);
  *
  * ```
  */
export const shouldZoomTo = (extent: __esri.Extent, view: __esri.MapView): boolean => {
  const {extent: mapExtent} = view;
  const width   = getPercent(mapExtent?.width, extent?.width);
  const height  = getPercent(mapExtent?.height, extent?.height);
  const smaller = isExtentSmallerThanMap(width, height);
  const bigger  = isExtentBiggerThanMap(width, height);
  const fits    = isExtentFitsInMap(width, height);

  return (!smaller && (fits || bigger));
};

/**
  * @showAssetDetails
  *
  * showAssetDetails method validates if we want to display the
  * Asset's Details based on the CVS Mode, selected Asset and breadcrumb
  *
  * @param   {boolean} isLargeFleetMode is CVS Mode
  * @param   {CVSAssetType} selectedAsset is the selected asset
  * @param   {Crumb[]} breadcrumbs is the Asset panelbreadcrumb path
  *
  * ```typescript
  *      import {showAssetDetails} from '@ion/dls/src/maps/helpers';
  *
  *      const shouldShowAssetDetails = showAssetDetails(extent, view);
  *
  * ```
  */
export const showAssetDetails = (
  isLargeFleetMode: boolean,
  selectedAsset?: CVSAssetType,
  breadcrumbs?: Crumb[]
): boolean => {
  if (isLargeFleetMode) {
    return !isNil(selectedAsset) &&
     (breadcrumbs?.length === BreadcrumbState.ASSET_DETAILS ||
      breadcrumbs?.length === BreadcrumbState.FIND_NEARBY);
  }
  return true;
};

/**
  * @getBreadcrumbs
  *
  * getBreadcrumbs method validates if adding asset details is needed
  *
  * @param   {Crumb[]} current is current breadcrumbs
  * @param   {string} label is the asset name
  * @param   {string} click is the event
  *
  * ```typescript
  *      import {getBreadcrumbs} from '@ion/dls/src/maps/helpers';
  *
  *      const breadcrumb = getBreadcrumbs(breadcrumb, label, click);
  *
  * ```
  */
export const getBreadcrumbs = (current: Crumb[], label: string, click: string): any[] => {
  return current?.length ? [...current] : [{label, click}];
};

/**
 * Resolve marker generator by Asset's event, environment and version
 */
const getAssetEventMarkerMethod = (
  env: AssetEnv,
  event: AssetEvent,
  version: AssetMarkerVersion
) => {
  if (version === VERSION.V2) {
    return V2_MARKERS[env]?.[event] ?? V1_MARKERS[event];
  }
  return V1_MARKERS[event];
};

/**
 * Resolve marker size based on version
 */
const getAssetEventMarkerSize = (
  version: AssetMarkerVersion,
  baseSize: number,
  v2Size: number
) => (version === VERSION.V2 ? v2Size : baseSize);

/**
 * Utility function that returns a picture-marker object that can be passed
 * to the esri Graphic constructor to make a graphic for Asset's events
 *
 * ```typescript
 *   import {createAssetEventMarker} from '@ion/dls/src/maps/helpers';
 *
 *  createAssetEventMarker({
 *    arrowColor : dlsColor('caBlue'),
 *    fillColor  : dlsColor('white'),
 *    angle      : 34,
 *    size       : 12
 *  })
 * ```
 */
export const createAssetEventMarker = ({
  fillColor = dlsColor('grey4'),
  arrowColor = dlsColor('white'),
  size = ASSET_HISTORY_ICON_SIZE.defaultSize,
  angle = 0,
  opacity = 1,
  version = VERSION.V1,
  env = ENVIRONMENT.US
}: AssetEventMarker) => {
  const method   = getAssetEventMarkerMethod(env, ASSET_EVENT.MOVING, version);
  const v2Size   = getOffsetIconSize(size);
  const iconSize = getAssetEventMarkerSize(version, size, v2Size);

  return {
    type   : 'picture-marker',
    url    : method({size: iconSize, fillColor, arrowColor, opacity}),
    angle  : angle,
    width  : iconSize,
    height : iconSize
  };
};

/**
 * Utility function that returns a picture-marker object that can be passed
 * to the esri Graphic constructor to make a graphic for start(origin) events
 *
 * ```typescript
 *   import {createAssetStartEventMarker} from '@ion/dls/src/maps/helpers';
 *
 *  createAssetStartEventMarker({
 *    fillColor : dlsColor('caBlue'),
 *    width     : 18,
 *    height    : 18
 *  })
 * ```
 */
export const createAssetStartEventMarker = ({
  fillColor = dlsColor('grey4'),
  width = 18,
  height = 18,
  version = VERSION.V1,
  env = ENVIRONMENT.US
}: AssetEventMarker) => {
  const method        = getAssetEventMarkerMethod(env, ASSET_EVENT.START, version);
  const currentWidth  = getAssetEventMarkerSize(version, width, ICON_OFFSET_SIZE.start.width);
  const currentHeight = getAssetEventMarkerSize(version, height, ICON_OFFSET_SIZE.start.height);

  return {
    type   : 'picture-marker',
    url    : method({fillColor, width: currentWidth, height: currentHeight}),
    width  : currentWidth,
    height : currentHeight
  };
};

/**
 * Utility function that returns a picture-marker object that can be passed
 * to the esri Graphic constructor to make a graphic for Stop(destination) events
 *
 * ```typescript
 *   import {createAssetStopEventMarker} from '@ion/dls/src/maps/helpers';
 *
 *  createAssetStopEventMarker({
 *    fillColor : dlsColor('caBlue'),
 *    width     : 18,
 *    height    : 25
 *  })
 * ```
 */
export const createAssetStopEventMarker = ({
  fillColor = dlsColor('grey4'),
  width = 18,
  height = 25,
  version = VERSION.V1,
  env = ENVIRONMENT.US
}: AssetEventMarker) => {
  const method        = getAssetEventMarkerMethod(env, ASSET_EVENT.STOP, version);
  const currentWidth  = getAssetEventMarkerSize(version, width, ICON_OFFSET_SIZE.stop.width);
  const currentHeight = getAssetEventMarkerSize(version, height, ICON_OFFSET_SIZE.stop.height);

  return {
    type   : 'picture-marker',
    url    : method({fillColor, width: currentWidth, height: currentHeight}),
    width  : currentWidth,
    height : currentHeight
  };
};

/**
 * Utility function that returns a picture-marker object that can be passed
 * to the esri Graphic constructor to make a graphic for Harsh events
 *
 * ```typescript
 *   import {createAssetHarshEventMarker} from '@ion/dls/src/maps/helpers';
 *
 *  createAssetHarshEventMarker({
 *    outlineColor : dlsColor('caBlue'),
 *    fillColor    : dlsColor('white'),
 *    angle        : 34,
 *    size         : 12
 *  })
 * ```
 */
export const createAssetHarshEventMarker = ({
  fillColor = dlsColor('caRed'),
  arrowColor = dlsColor('white'),
  size = ASSET_HISTORY_ICON_SIZE.defaultSize,
  angle = 0,
  opacity = 1,
  version = VERSION.V1,
  env = ENVIRONMENT.US
}: AssetEventMarker) => {
  const method   = getAssetEventMarkerMethod(env, ASSET_EVENT.MOVING, version);
  const iconsize = getAssetEventMarkerSize(version, size, ICON_OFFSET_SIZE.harshEvent);
  
  return {
    type   : 'picture-marker',
    url    : method({size: iconsize, fillColor, arrowColor, opacity}),
    angle  : angle,
    width  : iconsize,
    height : iconsize
  };
};

/**
 * Utility function that returns an object that can be passed
 * to the esri Graphic constructor to make a simple circle graphic
 */
export const createCircleMarker = ({
  size = 6,
  width = 1,
  fillColor = dlsColor('white'),
  outlineColor = dlsColor('grey4')
}: CircleMarkerParams) => ({
  type    : 'simple-marker',
  style   : 'circle',
  size,
  color   : fillColor,
  outline : {
    color : outlineColor,
    width
  }
});

/**
 * Creates a triangle direction marker object to be passed to the
 * Graphic constructor. SVG Path is derived from the DLS. Angle can
 * be passed directly from the heading value in HVS.
 *
 * ```typescript
 *   import {createTriangleMarker} from '@ion/dls/src/maps/helpers';
 *
 *  createTriangleMarker({
 *    outlineColor : dlsColor('caBlue'),
 *    fillColor    : dlsColor('white'),
 *    angle        : 34,
 *    width        : 4,
 *    size         : 12,
 *  })
 * ```
 */
export const createTriangleMarker = ({
  size = ASSET_HISTORY_ICON_SIZE.defaultSize,
  width = 1.5,
  fillColor = dlsColor('caRed'),
  outlineColor = dlsColor('white'),
  angle = 0
}: TriangleMarkerParams) => ({
  type    : 'simple-marker',
  style   : 'path',
  path    : EVENT_TRIANGLE_PATH,
  angle   : angle,
  size,
  color   : fillColor,
  outline : {
    color : outlineColor,
    width
  }
});

/**
 * creates a simple line object that can be passed to Graphic constructor
 *
 * ```typescript
 *   import {createSimpleLine} from '@ion/dls/src/maps/helpers';
 *
 *  createSimpleLine({
 *    color : dlsColor('caBlue'),
 *    width : 4
 *  })
 * ```
 */
export const createSimpleLine = ({
  color = dlsColor('grey4'),
  width = 2
}: SimpleLineParams) => ({
  type : 'simple-line',
  color,
  width
});

/**
 * creates a simple polyline object based on the passed paths array
 * that can be passed to Graphic constructor
 *
 * ```typescript
 *   import {createPolyLine} from '@ion/dls/src/maps/helpers';
 *
 *  createPoint(paths)
 * ```
 */
export const createPolyLine = ({
  paths
}: PolyLineParams) => ({
  type : 'polyline',
  paths
});

/**
 * creates a point object that can be passed to Graphic constructor
 *
 * ```typescript
 *   import {createPoint} from '@ion/dls/src/maps/helpers';
 *
 *  createPoint({longitude, latitude})
 * ```
 */
export const createPoint = ({
  longitude,
  latitude
}: PointParams) => ({
  type : 'point',
  longitude,
  latitude
});

/**
 * Map of graphics based on different event types
 *
 * ```typescript
 *    import {graphicMap} from '@ion/dls/src/maps/helpers';
 *
 *    const mappings = graphicsMap();
 *    mappings['startOfTripEvent']
 *    mappings.basicOutsideTripEvent
 * ```
 */
export const graphicMap = ({
  fillColor = dlsColor('skyBlue'),
  iconSize = ASSET_HISTORY_ICON_SIZE.smallSize,
  env = ENVIRONMENT.US,
  isAssetHistory = true,
  offset = false
}: GraphicMap) => {
  const version = getAssetMarkerVersion(offset);
  const nonActivePathParams = isAssetHistory
    ? {size: iconSize, outlineColor: fillColor, opacity: 0.5, offset, env}
    : {};
  return {
    basicInTripEvent : getBasicEventMarker({
      size         : iconSize,
      outlineColor : fillColor,
      offset,
      env
    },
    isAssetHistory, true
    ),
    basicOutsideTripEvent : getBasicEventMarker(nonActivePathParams, isAssetHistory, true),
    nonDirectionalEvent   : getBasicEventMarker({}, isAssetHistory),
    startOfTripEvent      : getStartOfTripEvent({fillColor, isAssetHistory, version, env}),
    endOfTripEvent        : getStopEventMarker({fillColor, isAssetHistory, version, env}),
    harshEvent            : (angle: number) => getHarshEventMarker({angle, version, env}, isAssetHistory)
  };
};

// Defines a utility to return Basic Event based on given parameters
const getBasicEventMarker = (params: any, isAssetHistory: boolean = false, direction = false) => {
  if (isAssetHistory && direction) {
    const eventParams = !isEmpty(params) ? {
      ...params,
      fillColor : params?.outlineColor,
      version   : getAssetMarkerVersion(params.offset)
    } : params;
    return createAssetEventMarker(eventParams);
  }
  return createCircleMarker(params);
};

type StartOfTripEventProps = {
  fillColor: string,
  isAssetHistory: boolean,
  version?: VERSION,
  env?: ENVIRONMENT
};

// Defines a utility to return Start of Trip event based on given parameters
const getStartOfTripEvent = ({
  fillColor,
  isAssetHistory = false,
  version = VERSION.V1,
  env = ENVIRONMENT.US
}: StartOfTripEventProps) => {
  if (!isAssetHistory) {
    return createCircleMarker({
      size         : 10,
      width        : 3,
      outlineColor : dlsColor('grey4')
    });
  }
  return createAssetStartEventMarker({
    width  : 18,
    height : 18,
    fillColor,
    version,
    env
  });
};

type StopOfTripEventProps = {
  fillColor: string,
  isAssetHistory: boolean,
  version?: VERSION,
  env?: ENVIRONMENT
};

// Defines a utility to return Stop Event based on given parameters
const getStopEventMarker = ({
  fillColor,
  isAssetHistory = false,
  version = VERSION.V1,
  env = ENVIRONMENT.US
}: StopOfTripEventProps) => {
  if (!isAssetHistory) {
    return createCircleMarker({
      size         : 10,
      width        : 3,
      outlineColor : fillColor
    });
  }
  return createAssetStopEventMarker({
    width  : 18,
    height : 25,
    fillColor,
    version,
    env
  });
};

// Defines a utility to return Harsh Event based on given parameters
const getHarshEventMarker = (params: any, isAssetHistory: boolean = false) => {
  return !isAssetHistory
    ? createTriangleMarker(params)
    : createAssetHarshEventMarker(params);
};

/**
 * Returns the key for the graphic that should be used based on trip data passed
 * to the function from the above graphic map
 *
 * ```typescript
 *   import {getGraphic} from '@ion/dls/src/maps/helpers';
 *
 *  getGraphic({
 *    element         : element,
 *    index           : 13,
 *    event           : event,
 *    tripView        : true,
 *    firstEventIndex : 10,
 *    lastEventIndex  : 24
 *  })
 * ```
 */
export const getGraphic = ({
  item,
  index,
  event = null,
  tripView = false,
  firstEventIndex = null,
  lastEventIndex = null,
  isStartOfTrip = false
}: GetGraphicParams) => {
  const {eventCode, id, isSevereEvent, tripGroup, ignition, heading} = item;
  const isIgOff     = isIgnitionOff(event);
  const inTrip      = tripView && (index > firstEventIndex && index <= lastEventIndex);
  const outsideTrip = tripView && (index <= firstEventIndex || index > lastEventIndex);
  // *Note that the order of the properties is important here. Since we are inverting the object
  // and taking the `true` key's value, if there is more than one, bracket notation will return the
  // most recent, so the last one in the object. So it's in order of least importance first.
  const eventTypes = {
    [`${SymbolTypes.startOfTripEvent}_${id}`]      : isStartOfTrip,
    [`${SymbolTypes.basicInTripEvent}_${id}`]      : inTrip,
    [`${SymbolTypes.basicOutsideTripEvent}_${id}`] : outsideTrip,
    [`${SymbolTypes.nonDirectionalEvent}_${id}`]   : !heading && !tripGroup,
    [`${SymbolTypes.endOfTripEvent}_${id}`]        : isFalse(ignition) && !!tripGroup,
    [`${SymbolTypes.harshEvent}_${id}`]            : isSevereEvent
  };
  const selectedEvent = invert(eventTypes).true;

  return selectedEvent || `${SymbolTypes.basicInTripEvent}_${id}`;
};

/**
 * layerExists
 *
 * Provides a utility which return if the Layer exists on the Map
 *
 * ```typescript
 *    import {layerExists} from '@ion/dls/src/maps/helpers';
 *
 *    const exists = layerExists(Map, 'history');
 *
 * ```
 */
export const layerExists = ({layers}: __esri.Map, layerId: string): boolean => {
  return (layers as any)?.items?.findIndex(({id}) => id === layerId) >= 0;
};

/**
 * getLayerById
 *
 * Provides a utility which return a layer from the map
 *
 * ```typescript
 *    import {getLayerById} from '@ion/dls/src/maps/helpers';
 *
 *    const layer = getLayerById(Map, 'history');
 *
 * ```
 */
export const getLayerById = ({layers}: {layers: {items: any[]}}, layerId: string): __esri.Layer => {
  return layers?.items?.find(({id}) => id === layerId);
};

/**
 * Returns the speed value only when the current state does not match the specified target State.
 * Otherwise returns an empty string.
 * ```typescript
 *  import {getSpeedByStatus} from '@/routes/tracking/trackingUtils';
 *  const speed = getSpeedByStatus(10, Parked, Moving); 10
 * 
 * ```
 */
export const getSpeedByStatus = (speed: number, currentState: string, targetState: string): number | '' =>
  currentState !== targetState ? speed : '';

/**
 * removeLayers
 *
 * Provides a utility which takes an array of layerId and remove them from the map
 *
 * ```typescript
 *    import {removeLayers} from '@ion/dls/src/maps/helpers';
 *
 *    removeLayers(map, ['LAYER_ID_1', 'LAYER_ID_2']);
 *
 * ```
 */
export const removeLayers = (map, layerList) => {
  layerList.forEach(layerId => map.layers.remove(getLayerById(map, layerId)));
};

/**
 * Provides a utility which takes a layerId and remove it from the map
 *
 * ```typescript
 *  import {removeLayerIfExist} from '@ion/dls/src/maps/helpers';
 *
 *  removeLayerIfExist(map, 'LAYER_ID');
 *
 * ```
 */
export const removeLayerIfExist = (map: __esri.Map, layerId: string) => {
  const layer = map?.layers?.find?.(layer => layer.id === layerId);
  if (layer) {
    map.layers.remove(layer);
  }
};

/**
 * reorderLayer
 *
 * Provides a utility which takes a layerId and reorder it to provided position
 *
 * ```typescript
 *    import {removeLayers} from '@ion/dls/src/maps/helpers';
 *
 *    reorderLayer(map, 'LAYER_ID', 0);
 *
 * ```
 */
export const reorderLayer = (map, layerId, position) => {
  map.layers.reorder(getLayerById(map, layerId), position);
};

/**
 * Defines the History Render by Asset
 */
export const getHistoryByAssetRenderer = ({
  assets,
  reportType,
  iconSize = ASSET_HISTORY_ICON_SIZE.smallSize,
  offset = false,
  env = ENVIRONMENT.US
}: HistoryByAssetRenderer) => {
  return {
    type          : 'unique-value',
    field         : 'rendererField',
    defaultSymbol : {
      type    : 'simple-marker',
      size    : 0,
      outline : {width: 0}
    },
    uniqueValueInfos : createUniqueValue({assets, reportType, iconSize, offset, env}),
    visualVariables  : [{
      type         : 'rotation',
      field        : 'bearing',
      rotationType : 'geographic'
    }]
  };
};

/**
 * Utility to create unique values for the History report renderer
 *
 * ```typescript
 *    import {createUniqueValue} from '@ion/dls/src/maps/helpers';
 *
 *    createUniqueValue(assets, type);
 *
 * ```
 */
export const createUniqueValue = ({
  assets,
  reportType,
  iconSize = ASSET_HISTORY_ICON_SIZE.smallSize,
  env = ENVIRONMENT.US,
  offset = false
}: CreateUniqueValue) => {
  const colorList        = getColorList();
  const uniqueValueInfos = [];
  const version          = getAssetMarkerVersion(offset);
  // If the report is of type 'Asset', add static unique values
  if (reportType === HistoryReportTypeEnum.Asset) {
    const historyByAssetUniqueValues = getLiveHistoryUniqueValues(version, env);
    uniqueValueInfos.push(...historyByAssetUniqueValues);
  }
  const uniqAssets  = uniqBy(assets, assetIdField);
  // Assign a unique color to each asset
  uniqAssets?.forEach((asset: HVSRecord, index: number) => {
    const colorValue = asset?.color || (colorList[index % colorList.length] || dlsColor('skyBlue'));
    uniqueValueInfos.push({
      value  : asset?.assetId?.toString(),
      symbol : createAssetEventMarker({fillColor: colorValue, size: iconSize, version, env})
    });
  });
  return uniqueValueInfos;
};

/**
 * Defines a utility to return Unique Value for the renderer of the Live History Layer.
 *
 * ```typescript
 *    import {getLiveHistoryUniqueValues} from '@ion/dls/src/maps/helpers';
 *
 *    const LiveHistoryUniqueValues = getLiveHistoryUniqueValues();
 *
 * ```
 */
export const getLiveHistoryUniqueValues = (
  version: AssetMarkerVersion = VERSION.V1,
  env: AssetEnv = ENVIRONMENT.US
) => {
  const ignitionEvents = getLiveHistoryIgnitionEvent(version, env);
  const stopEvents     = getLiveHistoryStopEvent(version, env);
  const batteryEvents  = getLiveHistoryBatteryEvent(version, env);
  const inputEvents    = getLiveHistoryInputEvent(version, env);
  const harshEvents    = getLiveHistoryHarshEvent(0, version);

  return [
    ...ignitionEvents,
    ...batteryEvents,
    ...harshEvents,
    stopEvents,
    inputEvents
  ];
};

// Defines a utility to return Ignition Events.
const getLiveHistoryIgnitionEvent = (version = VERSION.V1, env = ENVIRONMENT.US) => {
  const {ignOn, ignOff}     = HISTORY_EVENT_ICON_BY_ASSET;
  const {defaultSize: size} = ASSET_HISTORY_ICON_SIZE;

  const iginitonOn  = getAssetEventMarkerMethod(env, ASSET_EVENT.IGNITION_ON, version);
  const iginitonOff = getAssetEventMarkerMethod(env, ASSET_EVENT.IGNITION_OFF, version);
  const iconSize    = getAssetEventMarkerSize(version, size, ICON_OFFSET_SIZE.ignition);

  return [{
    value  : HistoryEventByAsset.IGON,
    symbol : {
      type   : 'picture-marker',
      url    : iginitonOn({size: iconSize, fillColor: ignOn.color}),
      width  : iconSize,
      height : iconSize
    }
  }, {
    value  : HistoryEventByAsset.IGOFF,
    symbol : {
      type   : 'picture-marker',
      url    : iginitonOff({size: iconSize, fillColor: ignOff.color}),
      width  : iconSize,
      height : iconSize
    }
  }];
};

// Defines a utility to return Stop Events.
const getLiveHistoryStopEvent = (version = VERSION.V1, env = ENVIRONMENT.US) => {
  const {stop}              = HISTORY_EVENT_ICON_BY_ASSET;
  const {defaultSize: size} = ASSET_HISTORY_ICON_SIZE;

  const method   = getAssetEventMarkerMethod(env, ASSET_EVENT.STOP_LIVE, version);
  const iconSize = getAssetEventMarkerSize(version, size, ICON_OFFSET_SIZE.stopLive);

  return {
    value  : HistoryEventByAsset.STOP,
    symbol : {
      type   : 'picture-marker',
      url    : method({size: iconSize, fillColor: stop.color}),
      width  : iconSize,
      height : iconSize
    }
  };
};

// Defines a utility to return Battery Events.
const getLiveHistoryBatteryEvent = (version = VERSION.V1, env = ENVIRONMENT.US) => {
  const {battLow}           = HISTORY_EVENT_ICON_BY_ASSET;
  const {defaultSize: size} = ASSET_HISTORY_ICON_SIZE;

  const method   = getAssetEventMarkerMethod(env, ASSET_EVENT.BATTERY, version);
  const iconSize = getAssetEventMarkerSize(version, size, ICON_OFFSET_SIZE.battery);

  const symbol = {
    type   : 'picture-marker',
    url    : method({size: iconSize, fillColor: battLow.color}),
    width  : iconSize,
    height : iconSize
  };
  return [{
    value : HistoryEventByAsset.ABATLO,
    symbol
  }, {
    value : HistoryEventByAsset.BBATLO,
    symbol
  }, {
    value : HistoryEventByAsset.VBATLO,
    symbol
  }];
};

// Defines a utility to return Input Events.
const getLiveHistoryInputEvent = (version = VERSION.V1, env = ENVIRONMENT.US) => {
  const {inputEvent}        = HISTORY_EVENT_ICON_BY_ASSET;
  const {defaultSize: size} = ASSET_HISTORY_ICON_SIZE;

  const method   = getAssetEventMarkerMethod(env, ASSET_EVENT.INPUT_EVENT, version);
  const iconSize = getAssetEventMarkerSize(version, size, ICON_OFFSET_SIZE.inputEvent);

  return {
    value  : HistoryEventByAsset.INPUTCHANGE,
    symbol : {
      type   : 'picture-marker',
      url    : method({size: iconSize, fillColor: inputEvent.color}),
      width  : iconSize,
      height : iconSize
    }
  };
};

// Defines a utility to return Harsh Events.
const getLiveHistoryHarshEvent = (
  angle: number = 0,
  version: AssetMarkerVersion = VERSION.V1,
  env: AssetEnv = ENVIRONMENT.US
) => {
  const eventTypes          = [];
  const {defaultSize: size} = ASSET_HISTORY_ICON_SIZE;
  const {harshEvent}        = HISTORY_EVENT_ICON_BY_ASSET;
  const severeEventTypeList = getSevereEventType();
  
  const method              = getAssetEventMarkerMethod(env, ASSET_EVENT.MOVING, version);
  const iconsize            = getAssetEventMarkerSize(version, size, ICON_OFFSET_SIZE.harshEvent);
  
  severeEventTypeList?.forEach(eventType => {
    eventTypes.push({
      value  : eventType,
      symbol : {
        type   : 'picture-marker',
        url    : method({size: iconsize, fillColor: harshEvent.color}),
        angle  : angle,
        width  : iconsize,
        height : iconsize
      }
    });
  });
  return eventTypes;
};

// Defines a utility to return array of severe event types.
export const getSevereEventType = () => {
  const severeEventType = getEventCodesByClassification(
    translate(EventClassificationEntriesMap[EventClassification.Severe]),
    getEventList(),
    'eventType'
  );
  return severeEventType || [];
};

// returns object with properties needed to build direction marker, eventType and bearing
export const addDirectionMarker = (currentEvent: LatLong, previousEvent: LatLong): {
  bearing: number;
  eventType: HistoryEventByAsset;
  latitude: number;
  longitude: number;
} => {
  const {
    latitude: lat1,
    longitude: long1
  } = currentEvent;
  const {
    latitude: lat2,
    longitude: long2
  } = previousEvent;
  const distanceBetween = getDistanceFromLatLong({lat1, long1, lat2, long2});
  // Don't render a marker if the distance between 2 events is very small
  if (distanceBetween > MIN_DISTANCE_FOR_DIRECTION_ARROW) {
    const {latitude, longitude} = getMidPoint({lat1, long1, lat2, long2});
    return {
      bearing   : bearing({lat1, long1, lat2, long2}),
      eventType : HistoryEventByAsset.DIRECTION,
      latitude,
      longitude
    };
  }
};

// formula returning lat long of midpoint of two points
export const getMidPoint = ({lat1, long1, lat2, long2}: LatLongGroup): LatLong => {
  // convert to radians
  const longitudeDiff = degreeToRadius(long2 - long1);
  const latitude1     = degreeToRadius(lat1);
  const latitude2     = degreeToRadius(lat2);
  const longitude1    = degreeToRadius(long1);

  const x             = Math.cos(latitude2) * Math.cos(longitudeDiff);
  const y             = Math.cos(latitude2) * Math.sin(longitudeDiff);
  const midLatitude   = Math.atan2(
    Math.sin(latitude1) + Math.sin(latitude2), Math.sqrt((Math.cos(latitude1) + x) * (Math.cos(latitude1) + x) + y * y)
  );
  const midLongitude  = longitude1 + Math.atan2(y, Math.cos(latitude1) + x);

  // return in degrees
  return {
    latitude  : toDegrees(midLatitude),
    longitude : toDegrees(midLongitude)
  };
};

// Calculates the distance between two points
export const getDistanceFromLatLong = (
  {lat1, long1, lat2, long2}: LatLongGroup,
  metric = false
): number => {
  const radiusMi = 3958.8; // Radius of the earth in miles
  const radiusKm = 6371; // Radius of the earth in km
  const radius   = metric ? radiusKm : radiusMi;
  const dLat     = degreeToRadius(lat2 - lat1);
  const dLon     = degreeToRadius(long2 - long1);
  const x        = Math.sin(dLat / 2);
  const y        = Math.sin(dLon / 2);
  const d        = Math.cos(degreeToRadius(lat1)) * Math.cos(degreeToRadius(lat2));
  const a        = ((x * x) + (d * (y * y)));
  const c        = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = radius * c;
  return distance;
};

// Get bearing between 2 points
export const bearing = ({lat1, long1, lat2, long2}: LatLongGroup): number => {
  const startLat      = degreeToRadius(lat1);
  const startLng      = degreeToRadius(long1);
  const destLat       = degreeToRadius(lat2);
  const destLng       = degreeToRadius(long2);
  const y             = Math.sin(destLng - startLng) * Math.cos(destLat);
  const x             = Math.cos(startLat) * Math.sin(destLat) -
    Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng);
  const radianBearing = Math.atan2(y, x);
  const bearing       = toDegrees(radianBearing);
  return (bearing + 360) % 360;
};

// Convert degrees to radius
export const degreeToRadius = (deg: number): number => {
  return deg * (Math.PI / 180);
};

// Converts radians to degrees
export const toDegrees = (radians: number): number => {
  return radians * 180 / Math.PI;
};

/**
 * Color for the connecting line by Asset
 */
export const getHistoryColors = () => ({
  caBlue     : dlsColor('caBlue'),
  caGreen    : dlsColor('caGreen'),
  arcticBlue : dlsColor('arcticBlue'),
  pink       : dlsAlt('maps', 'colors', 'pink'),
  caYellow   : dlsColor('caYellow'),
  lightTeal  : dlsColor('lightTeal'),
  midnight   : dlsAlt('maps', 'colors', 'midnight'),
  orange     : dlsAlt('maps', 'colors', 'orange'),
  chocolate  : dlsAlt('maps', 'colors', 'chocolate'),
  green      : dlsAlt('maps', 'colors', 'green')
});

/**
 * History Input Event Color
 */
export const getInputEventColors = () => ({
  caBlue     : dlsColor('caBlue'),
  orange     : dlsAlt('maps', 'colors', 'orange'),
  green      : dlsAlt('maps', 'colors', 'green'),
  pink       : dlsAlt('maps', 'colors', 'pink'),
  darkGreen  : dlsAlt('maps', 'colors', 'darkGreen'),
  darkTeal   : dlsAlt('maps', 'colors', 'darkTeal'),
  aaBlue     : dlsAlt('maps', 'colors', 'aaBlue'),
  yellow     : dlsAlt('maps', 'colors', 'yellow'),
  darkBlue   : dlsAlt('maps', 'colors', 'darkBlue'),
  darkPurple : dlsAlt('maps', 'colors', 'darkPurple')
});

// Getting a list of Colors
const getColorList = () => Object.values(getHistoryColors());

// Default width for the Path Line
const PATH_WIDTH = '6px';

/**
 * Defines a utility to create connecting line graphics by Asset
 */
export const createLayerInfoByAsset = async(features: HVSRecord[]) => {
  const colorList    = getColorList();
  const graphics     = [];
  const uniqueValues = [];
  const [Graphic]    = await initializeEsriModules('esri/Graphic');
  const uniqAssets   = uniqBy(features, assetIdField);
  uniqAssets?.forEach((asset: HVSRecord, index: number) => {
    const assets      = getSortedEvents(features, assetIdField, asset?.assetId);
    const coordinates = getCoordinates(assets);

    const polyline = {
      type  : 'polyline',
      paths : coordinates
    };
    const polylineSymbol = {
      type  : 'simple-line',
      color : asset?.color || (colorList[index % colorList.length] || dlsColor('grey1')),
      width : PATH_WIDTH,
      style : 'solid'
    };

    uniqueValues.push({
      value  : asset?.assetId,
      symbol : polylineSymbol
    });

    graphics.push(new Graphic({
      attributes : asset,
      geometry   : polyline,
      symbol     : polylineSymbol
    }));
  });
  return {graphics, uniqueValues};
};

/**
 * Defines a utility to create a renderer for lines
 */
export const createEventLineRenderer = ({uniqueValues, visualVariables, reportField, color}: EventLineLayer) => {
  let renderer: {
    visualVariables?: unknown;
    field?: unknown;
    type?: string;
    defaultSymbol?: unknown,
    uniqueValueInfos?: unknown
  } = {};
  if (!isEmpty(uniqueValues)) {
    const otherSym = {
      type  : 'simple-line',
      color : color || dlsColor('skyBlue'),
      width : PATH_WIDTH,
      style : 'solid'
    };
    renderer = {
      field            : reportField,
      type             : 'unique-value',
      defaultSymbol    : otherSym,
      uniqueValueInfos : uniqueValues
    };

    if (visualVariables) {
      renderer.visualVariables = visualVariables;
    }
    return renderer;
  }
};

/**
 * Groups the input features by their asset ID.
 * This enables per-asset path generation in the final visualization.
 */
const groupFeaturesByAssetId = (features: HVSRecord[]): Map<string, HVSRecord[]> => {
  const map = new Map<string, HVSRecord[]>();
  for (const feature of features) {
    const assetId = feature[assetIdField];
    if (!map.has(assetId)) {
      map.set(assetId, []);
    }
    map.get(assetId)!.push(feature);
  }
  return map;
};

/**
 * Sorts and enriches raw event records with calculated metadata:
 * - Adds `reportType` derived from feature inputs.
 * - Adds `timeDiffInHours` relative to current time.
 */
const enrichEventsWithMetadata = (
  events: HVSRecord[],
  assetId: string,
  historyInputEvents: HistoryInputEvents[]
): any[] => {
  const sortedEvents = getSortedEvents(events, assetIdField, assetId);
  return sortedEvents.map(event => ({
    ...event,
    reportType      : createReportTypeField(event, historyInputEvents),
    timeDiffInHours : Math.round(timeDiff(Date.now(), event.eventTime, 'hours'))
  }));
};

/**
 * Groups consecutive events with the same eventTime and reportType.
 * This reduces visual clutter by creating a single polyline per group.
 */
const groupConsecutiveEvents = (records: HVSRecord[]): any[][] => {
  const groups: any[][]   = [];
  let currentGroup: any[] = [];

  for (const record of records) {
    const prev        = currentGroup.at(-1);
    const isSameGroup =
      prev &&
      record.eventTime === prev.eventTime &&
      record.reportType === prev.reportType;

    if (isSameGroup) {
      currentGroup.push(record);
    } else {
      if (currentGroup.length >= 2) {
        groups.push(currentGroup);
      }
      currentGroup = [record];
    }
  }

  if (currentGroup.length >= 2) {
    groups.push(currentGroup);
  }

  return groups;
};

type CreatePolylineGraphicProps = {
  group: any[],
  inputMap: Map<string, HistoryInputEvents>,
  seenReportTypes: Set<string>,
  uniqueValues: any[]
};
/**
 * Creates an ESRI polyline graphic from a group of events.
 * Skips groups with unselected report types or missing coordinates.
 * Adds symbology to the legend if the report type is new.
 */
const createPolylineGraphic = ({
  group,
  inputMap,
  seenReportTypes,
  uniqueValues
}: CreatePolylineGraphicProps): __esri.Graphic | null => {
  const reportType = group[0].reportType;
  const isMixed    = isMixedSelected(reportType, Array.from(inputMap.values()));
  const input      = inputMap.get(isMixed ? HistoryInputEventsEnum.Mixed : reportType);

  if (!input?.isSelected) return null;

  const coordinates = getCoordinates(group);
  if (!coordinates?.length) return null;

  const polylineSymbol = {
    type  : 'simple-line',
    color : input.color || dlsColor('grey2'),
    width : PATH_WIDTH,
    style : 'solid'
  };

  if (!seenReportTypes.has(reportType)) {
    seenReportTypes.add(reportType);
    uniqueValues.push({value: reportType, symbol: polylineSymbol});
  }

  return new Graphic({
    attributes : group[0],
    geometry   : {type: 'polyline', paths: coordinates},
    symbol     : polylineSymbol
  });
};

type ProcessGroupEventsProps = {
  groups: any[][],
  inputMap: Map<string, HistoryInputEvents>,
  seenReportTypes: Set<string>,
  uniqueValues: any[],
  batchCounter: {count: number},
  BATCH_SIZE?: number
};

/**
 * Processes all event groups for a single asset:
 * - Creates graphics per group.
 * - Manages batching to avoid UI freezing.
 * - Updates unique report types for the symbology legend.
 */
const processGroupedEvents = async({
  groups,
  inputMap,
  seenReportTypes,
  uniqueValues,
  batchCounter,
  BATCH_SIZE = 250
}: ProcessGroupEventsProps): Promise<__esri.Graphic[]> => {
  const graphics: __esri.Graphic[] = [];
  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  for (const group of groups) {
    const graphic = createPolylineGraphic({group, inputMap, seenReportTypes, uniqueValues});
    if (graphic) {
      graphics.push(graphic);
      if (++batchCounter.count % BATCH_SIZE === 0) {
        await wait(0);
      }
    }
  }

  return graphics;
};

/**
 * Main controller function to generate historical path polylines for assets:
 * - Groups features by asset.
 * - Enriches event data.
 * - Groups similar events.
 * - Draws simplified paths with legend data.
 * - Batches processing to keep the browser responsive.
 */
export const createLayerInfoByEvent = async(
  features: HVSRecord[],
  historyInputEvents: HistoryInputEvents[],
  hours: number
) => {
  const graphics: __esri.Graphic[] = [];
  const uniqueValues: any[]        = [];
  const seenReportTypes            = new Set<string>();
  const visualVariables            = createVisualVariables(hours);

  const featuresByAssetId = groupFeaturesByAssetId(features);
  const inputMap          = new Map(historyInputEvents.map(evt => [evt.label, evt]));
  const batchCounter      = {count: 0};

  for (const [assetId, events] of featuresByAssetId) {
    const enrichedRecords = enrichEventsWithMetadata(events, assetId, historyInputEvents);
    const groupedRecords  = groupConsecutiveEvents(enrichedRecords);

    const assetGraphics = await processGroupedEvents({
      groups          : groupedRecords,
      inputMap        : inputMap,
      seenReportTypes : seenReportTypes,
      batchCounter    : batchCounter,
      uniqueValues
    });
    graphics.push(...assetGraphics);
  }

  return {graphics, uniqueValues, visualVariables};
};

/**
 * Defines a utility to return report type by Input [Plow, Spreader, Both]
 */
const getReportTypeField = (asset: HVSRecord): string => {
  const {None}        = HistoryInputEventsEnum;
  const defaultInputs = Object.values(HistoryDefaultInputsEnum);
  let reportType      = None;
  defaultInputs?.forEach(input => {
    try {
      const {label, active} = safeJSONParse(asset[input]);
      if (label && !defaultInputs?.includes(label) && active) {
        reportType = reportType === None ? label : `${reportType}${HISTORY_SEPARATOR}${label}`;
      }
    } catch (err) {
      error(err);
    }
  });
  return reportType;
};

/**
 * Utility to add an attribute to the asset's feature during
 * renderer creation for Live History – View by Input.
 */
const createReportTypeField = (asset: HVSRecord, historyInputEvents: any) => {
  if (!hasInputSelected(historyInputEvents)) {
    return HistoryInputEventsEnum.None;
  }

  const inputSelectedLabels   = getInputSelectedLabels(historyInputEvents);
  const defaultInputs         = Object.values(HistoryDefaultInputsEnum);
  const assetInputs: string[] = [];

  defaultInputs?.forEach(input => {
    try {
      const {label, active} = safeJSONParse(asset[input]);
      if (label && !defaultInputs?.includes(label) && active) {
        assetInputs.push(label);
      }
    } catch (err) {
      error(err);
    }
  });

  if (assetInputs?.length && inputSelectedLabels?.length) {
    if (inputSelectedLabels.length === 1) {
      const label = inputSelectedLabels[0];
      if (assetInputs.includes(label)) {
        return label;
      }
    } else if (isMixedInput(inputSelectedLabels, assetInputs)) {
      return HistoryInputEventsEnum.Mixed;
    } else {
      const input = getFirstMatchingInput(inputSelectedLabels, assetInputs);
      if (input) {
        return input;
      }
    }
  }
  return HistoryInputEventsEnum.None;
};

// Returns true if at least two inputs are selected
export const hasInputSelected = (inputs: any[]): boolean =>
  inputs?.filter(input => input.isSelected).length >= 2;

// Checks if all selected labels exist in the asset input list
export const isMixedInput = (selected: string[], assetInputs: string[]): boolean =>
  selected.every(label => assetInputs.includes(label));

// Returns the first matching input
export const getFirstMatchingInput = (selected: string[], assetInputs: string[]): string | undefined =>
  selected.find(label => assetInputs.includes(label));

// Filters selected input labels, excluding "None" and "Mixed"
export const getInputSelectedLabels = (inputs: any[]): string[] =>
  inputs
    ?.filter(input =>
      input.isSelected &&
      input.label !== HistoryInputEventsEnum.None &&
      input.label !== HistoryInputEventsEnum.Mixed
    )
    .map(input => input.label);

/**
 * Defines a utility to create Esri Visual Variables object
 */
const createVisualVariables = (hours: number): any[] => {
  if (isNumber(hours)) {
    return [{
      type  : 'opacity',
      field : 'timeDiffInHours',
      stops : [
        {value: (hours * 0.12), opacity: 1},
        {value: (hours * 0.25), opacity: 0.75},
        {value: (hours * 0.50), opacity: 0.50},
        {value: (hours * 0.75), opacity: 0.25},
        {value: hours, opacity: 0.12}
      ]
    }];
  }
  return [];
};

/**
 * Defines a utility to return svg image
 */
export const getInputEventColorBar = ({
  value,
  label,
  adornment,
  width,
  height,
  color,
  labelColor,
  fontStyle
}: HistoryInputEventsColorBar) => {
  const svg = (`
    <svg width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'
    xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'>
      <style>.label {${fontStyle}}</style>
      <text x='6' y='10' fill='${labelColor}' class='label'>${label}</text>
      <text x='36' y='10' fill='${labelColor}' class='label'>${(Math.round((value * 0.25) * 10) / 10)}</text>
      <text x='74' y='10' fill='${labelColor}' class='label'>${(Math.round((value * 0.50) * 10) / 10)}</text>
      <text x='113' y='10' fill='${labelColor}' class='label'>${(Math.round((value * 0.75) * 10) / 10)}</text>
      <text x='150' y='10' fill='${labelColor}' class='label'>${value} ${adornment}</text>
      <defs>
        <path d='M5.71 15.05L42.75 15.05L42.75 20.81L5.71 20.81L5.71 15.05Z' id='bnTNJPQPF'></path>
        <path d='M42.5 13.93L43.86 13.93L43.86 21.41L42.5 21.41L42.5 13.93Z' id='f4tXv66ycP'></path>
        <path d='M43.71 15.05L80.75 15.05L80.75 20.81L43.71 20.81L43.71 15.05Z' id='cdr1ebk7V'></path>
        <path d='M80.5 13.93L81.86 13.93L81.86 21.41L80.5 21.41L80.5 13.93Z' id='acxJQLa6H'></path>
        <path d='M81.71 15.05L118.75 15.05L118.75 20.81L81.71 20.81L81.71 15.05Z' id='aDnJc6fJk'></path>
        <path d='M118.5 13.93L119.86 13.93L119.86 21.41L118.5 21.41L118.5 13.93Z' id='a88ftCJU5Y'></path>
        <path d='M119.71 15.05L156.75 15.05L156.75 20.81L119.71 20.81L119.71 15.05Z' id='e2ipuQUbOQ'></path>
        <path d='M156.5 13.93L157.86 13.93L157.86 21.41L156.5 21.41L156.5 13.93Z' id='acBQyIfK3'></path>
        <path d='M157.71 15.05L194.75 15.05L194.75 20.81L157.71 20.81L157.71 15.05Z' id='d6TePdGX1a'></path>
      </defs>
      <g>
        <g><use xlink:href='#bnTNJPQPF' opacity='1' fill='${color}' fill-opacity='1'></use></g>
        <g><use xlink:href='#f4tXv66ycP' opacity='1' fill='#c9c5c9' fill-opacity='1'></use></g>
        <g><use xlink:href='#cdr1ebk7V' opacity='1' fill='${color}${'BF'}' fill-opacity='1'></use></g>
        <g><use xlink:href='#acxJQLa6H' opacity='1' fill='#c9c5c9' fill-opacity='1'></use></g>
        <g><use xlink:href='#aDnJc6fJk' opacity='1' fill='${color}${80}' fill-opacity='1'></use></g>
        <g><use xlink:href='#a88ftCJU5Y' opacity='1' fill='#c9c5c9' fill-opacity='1'></use></g>
        <g><use xlink:href='#e2ipuQUbOQ' opacity='1' fill='${color}${40}' fill-opacity='1'></use></g>
        <g><use xlink:href='#acBQyIfK3' opacity='1' fill='#c9c5c9' fill-opacity='1'></use></g>
        <g><use xlink:href='#d6TePdGX1a' opacity='1' fill='${color}${'1F'}' fill-opacity='1'></use></g>
      </g>
    </svg>`);
  return `data:image/svg+xml;base64,${btoa(svg.trim())}`;
};

/**
 * Defines a utility to return coordinates
 */
export const getCoordinates = (assets: CVSAssetType[]): number[][] => {
  return assets?.map(({longitude, latitude}) => [longitude, latitude]);
};

/**
 * Defines a utility to verify if two inputs are selected
 */
const isMixedSelected = (reportType: string, historyInputEvents: HistoryInputEvents[]): boolean => {
  const hasMixed   = reportType?.includes(HISTORY_SEPARATOR);
  const inputMixed = historyInputEvents?.find(({label, isSelected}) =>
    label === HistoryInputEventsEnum.Mixed && isSelected);
  if (hasMixed && inputMixed) {
    const events = getMixedInputEvents(inputMixed);
    if (isArray(events)) {
      return reportType === events[0] || reportType === events[1];
    }
  }
  return false;
};

/**
 * Defines a utility to return the Input Mixed Selected
 */
export const getInputMixedSelection = (inputs: any[], maxInputSelected: number): object => {
  const {Mixed}         = HistoryInputEventsEnum;
  const inputSelected   = inputs?.filter(({isSelected}) => isSelected);
  const inputLabels     = inputSelected?.filter(({color}) => color).map(({label}) => label);
  const isMixedSelected = inputSelected.length === maxInputSelected;
  return {
    label      : Mixed,
    isSelected : isMixedSelected,
    color      : dlsAlt('maps', 'colors', 'purple'),
    assetNames : isMixedSelected ? inputLabels : []
  };
};

/**
 * Defines a utility to return sorted features
 */
export const getSortedEvents = (features: CVSAssetType[], field: string, value: any): CVSAssetType[] => {
  return sortby(features?.filter(item => item[field] === value), ['eventTime']);
};

/**
 * Defines a utility to return sorted features by Input Event
 */
const getSortedEventByInput = (features: CVSAssetType[], field: string, input: any): CVSAssetType[] => {
  if (input.label === HistoryInputEventsEnum.Mixed) {
    const events = getMixedInputEvents(input);
    if (isArray(events)) {
      return sortby(features?.filter(item => item[field] === events[0] ||
        item[field] === events[1]), ['eventTime']);
    }
  }
  return getSortedEvents(features, field, input?.label);
};

/**
 * Defines a utility to return the input events when selecting two inputs
 *
 * Its returning only two combinations because only two selections allowed
 */
const getMixedInputEvents = (input: any): string[] => {
  if (input.label === HistoryInputEventsEnum.Mixed &&
      input?.assetNames?.length > 1) {
    const {assetNames} = input;
    const opt1 = `${assetNames[0]}${HISTORY_SEPARATOR}${assetNames[1]}`;
    const opt2 = `${assetNames[1]}${HISTORY_SEPARATOR}${assetNames[0]}`;
    return [opt1, opt2];
  }
  return input?.label;
};

/**
 * Defines a utility to return if a value exists in the collection
 */
const hasValue = (uniqueValues: any, value: string): boolean => {
  return Boolean(uniqueValues?.findIndex(({value: current}) => current === value) >= 0);
};

/* The map utility function provides the map points that are in map respect to
 * the coordinates, screen, edge and modified latiutude and longitude.
 *
 * ```typescript
 *
 *  import {getMapPoints} from '../helpers';
 *
 *  const {
 *    vertices,
 *    centerPoint,
 *    edgePoint
 *  } = getMapPoints({
 *    mapView,
 *    mapPointLongitude,
 *    mapPointLatitude,
 *    newLatitude,
 *    newLongitude
 *  });
 *
 * ```
 *
 * @param mapView Esri map view object
 * @param mapPointLatitude Actual latitude in map coordinates
 * @param mapPointLongitude Actual longitude in map coordinates
 * @param newLongitude Modified longitude in map coordinates
 * @param newLatitude Modified latitude in map coordinates
 * @returns Vertices, edge map point, edge point, edge screen point,
 *  center map point, center point, center screen point
 */
export const getMapPoints = ({
  mapView,
  mapPointLongitude,
  mapPointLatitude,
  newLongitude,
  newLatitude
}: GetMapPointsParams) => {
  const centerMapPoint = {
    x                : mapPointLongitude,
    y                : mapPointLatitude,
    spatialReference : {
      wkid : 4326
    }
  };
  const centerScreenPoint = mapView.toScreen(centerMapPoint as Point);
  const centerPoint = mapView.toMap({
    x : centerScreenPoint?.x,
    y : centerScreenPoint?.y
  });

  const edgeMapPoint = {
    x                : newLongitude,
    y                : newLatitude,
    spatialReference : {
      wkid : 4326
    }
  };
  const edgeScreenPoint = mapView.toScreen(edgeMapPoint as Point);
  const edgePoint = mapView.toMap({
    x : edgeScreenPoint?.x,
    y : edgeScreenPoint?.y
  });
  const vertices = [
    [centerPoint?.x, centerPoint?.y],
    [edgePoint?.x, edgePoint?.y]
  ];
  return {
    vertices,
    edgeMapPoint,
    edgePoint,
    edgeScreenPoint,
    centerMapPoint,
    centerPoint,
    centerScreenPoint
  };
};

/* The map utility function provides the Operational Layers from the Map
 *
 * ```typescript
 *
 *  import {augmentOperationalLayers} from '../helpers';
 *
 *  const operationalLayers = augmentOperationalLayers(map);
 *
 * @param map from the webmap
 */
export const augmentOperationalLayers = ({layers}: __esri.Map): __esri.Layer[] => {
  const operationalLayers = [];
  const filtedlayers = excludeMapLayers(layers?.items);
  filtedlayers?.forEach((layer) => {
    const operationlayer = augmentLayer(layer);
    operationlayer && operationalLayers.push(operationlayer);
  });
  return operationalLayers;
};

/**
 * Defines a utility to return the layers
 */
const augmentLayer = (layer: __esri.Layer): Layer => {
  if (layer) {
    const {
      id,
      title,
      opacity,
      visible,
      url,
      operationalLayerType
    } = layer;
    return {
      id,
      opacity,
      title,
      url,
      layerType  : operationalLayerType,
      layers     : augmentSublayers(layer),
      visibility : visible
    };
  }
  return null;
};

/**
 * Defines a utility to return the sublayers
 */
const augmentSublayers = ({sublayers}: __esri.Layer): Sublayer[] => {
  const response = [];
  //Nested method to get sublayers
  function getSublayers(sublayers: __esri.Sublayer) {
    const {items} = sublayers || {};
    items?.forEach(item => {
      const {id, title, visible} = item || {};
      response.push({
        id,
        name              : title,
        parentLayerId     : getParentLayerId(item),
        defaultVisibility : visible
      });
      getSublayers(item?.sublayers);
    });
    return response;
  };

  if (sublayers) {
    return sortby(getSublayers(sublayers), ['id']);
  }
  return null;
};

/**
 * Defines a utility to return the parent layer id
 */
const getParentLayerId = ({parent}: __esri.Layer): number => {
  const defaultValue = -1;
  if (parent) {
    const {id} = parent || {};
    return isNumber(id) ? id : defaultValue;
  }
  return defaultValue;
};

/**
 * Filter to get allowed operational layers
 * TODO: We won't need this once the WebMaps contains
 * only necessary layers
*/
const excludeMapLayers = (operationalLayers: __esri.Layer[]): __esri.Layer[] => {
  const hardCodedLayers = Object.values(CurrentMapLayer);
  return operationalLayers?.filter((layer) => {
    return hardCodedLayers.findIndex((title) => title === layer?.title) < 0;
  });
};

/**
 * getAssetsAggregationsData method retrieves the CVS Assets based on aggregation levels
 *
 * ```typescript
 *
 *  import {getAssetsAggregationsData} from './helpers';
 *
 *  const assetsAggregations = getAssetsAggregationsData(current, updated);
 *
 * ```
 */
export const getAssetsAggregationsData = (
  current: CVSAssetAggregations[],
  response: any
): CVSAssetAggregations[] => {
  const {method, minZoomLevel, maxZoomLevel} = response?.data;
  const aggregation = {
    features  : parseCollection(response, ['attributes', 'geometry']),
    timestamp : Date.now(),
    minZoomLevel,
    maxZoomLevel,
    method
  };
  const assetsAggregation = unionBy([aggregation], current, 'method');
  return assetsAggregation;
};

/**
 * getAssetsAggregationData method retrieves the assets aggregation data
 * based on method (level)
 *
 * ```typescript
 *
 *  import {getAssetsAggregationData} from './helpers';
 *
 *  const aggregationLevel = getAssetsAggregationData(current, method);
 *
 * ```
 */
export const getAssetsAggregationData = (
  current: CVSAssetAggregations[],
  method: string
): CVSAssetAggregations => {
  return current?.find(item => item?.method === method);
};

/**
 * getAssetsAggregationLevel method retrieves the assets aggregation level
 *
 * ```typescript
 *
 *  import {getAssetsAggregationLevel} from './helpers';
 *
 *  const aggregationLevel = getAssetsAggregationLevel(current, viewZoom);
 *
 * ```
 */
export const getAssetsAggregationLevel = (
  current: CVSAssetAggregations[],
  viewZoom: number
) => {
  let level = DEFAULT_AGGREGATION_LEVEL;
  current?.forEach((item) => {
    const {minZoomLevel, maxZoomLevel} = item;
    if (inRange(viewZoom, minZoomLevel, maxZoomLevel)) {
      level = item?.method;
    }
  });
  return level;
};

/**
 * shouldQueryCVSService method retrieves if we should query CVS Service or use the
 * the client side data based on the aggregation level data on the client side.
 *
 * ```typescript
 *
 *  import {shouldQueryCVSService} from './helpers';
 *
 *  const shouldQueryCVS = shouldQueryCVSService(current, viewZoom, interval);
 *
 * ```
 */
export const shouldQueryCVSService = (
  current: CVSAssetAggregations[],
  viewZoom: number,
  interval: number
) => {
  const level       = getAssetsAggregationLevel(current, viewZoom);
  const aggregation = getAssetsAggregationData(current, level);
  if (aggregation && level !== DEFAULT_AGGREGATION_LEVEL) {
    return ((Date.now() - aggregation?.timestamp) / 60000) > interval;
  }
  return true;
};

/**
 * getTrackingAssets method retrieves the AVL events based on the mode [Normal / HFM]
 *
 * ```typescript
 *
 *  import {getTrackingAssets} from './helpers';
 *
 *  const assets = getTrackingAssets({assetsNormal, assetsLarge, viewZoom, isLargeFleet});
 *
 * ```
 */
export const getTrackingAssets = ({
  assetsNormalFleet,
  assetsLargeFleet,
  isLargeFleet,
  viewZoom
}: GetTrackingAssets): CVSAssetType[] => {
  if (!isLargeFleet && assetsNormalFleet) {
    const nfmAssets = assetsNormalFleet
      .map(item => item?.getDevice?.());
    return nfmAssets;
  }
  if (assetsLargeFleet) {
    const level  = getAssetsAggregationLevel(assetsLargeFleet, viewZoom);
    const assets = getAssetsAggregationData(assetsLargeFleet, level);
    return assets?.features || [];
  }
  return [];
};

/**
 * getMapZoomLevel returns the map leve based on the fleet mode
 * and basemap
 *
 * ```typescript
 *
 * import {getMapZoomLevel} from '@ion/dls/src/maps/helpers';
 *
 * const mapLevel = getMapZoomLevel(nodes, Graphic);
 * ...
 */
export const getMapZoomLevel = (mapLevel: number, baseMap: string, isLargeFleet: boolean) => {
  if ((baseMap === BaseMapEnum.grayVector || baseMap === BaseMapEnum.streetsNavigationVector) &&
    isLargeFleet) {
    return mapLevel + 1;
  }
  return mapLevel;
};

/**
 * drawNodes returns an array of Graphics of nodes containing
 * coordinates and attributes of event nodes
 *
 * ```typescript
 *
 * import {drawNodes} from '@ion/dls/src/maps/helpers';
 *
 * const nodes = drawNodes(nodes, Graphic);
 * ...
 *
 * ```
 */
export const drawNodes = (nodes, Graphic, mapFields?: GenericObject[]) => {
  nodes = nodes.filter(elem => elem.mapVisibility === true);
  let isStartOfTrip;
  const symbolGraphics = nodes.map((element, index) => {
    const {
      longitude: elementLongitude = null,
      latitude: elementLatitude = null,
      gridVisibility
    } = element;

    if (!(nodes[index-1]?.tripGroup) && element.tripGroup) {
      isStartOfTrip = true;
    }

    const point = gridVisibility ? createPoint({
      longitude : elementLongitude,
      latitude  : elementLatitude
    }) : null;
    const node = esriTypeCast(element, mapFields);
    const graphic = new Graphic({
      attributes : {
        ...node,
        symbolType : getGraphic({
          item  : node,
          event : node,
          index,
          isStartOfTrip
        })
      },
      geometry : point
    });
    isStartOfTrip = false;
    return graphic;
  });
  return symbolGraphics;
};

/**
 * drawPath returns an array of Graphics of paths containing
 * coordinates and eventTime of path
 *
 * If the boundary parameter is passed then this process should
 * validate if the items are in sequence order, otherwise, it should add any item.
 *
 * ```typescript
 *
 * import {drawPath} from '@ion/dls/src/maps/helpers';
 *
 * const paths = drawPath(routes, Graphic, boundary);
 * ...
 *
 * ```
 */
export const drawPath = (
  routes: HVSRecord[],
  Graphic: typeof __esri.Graphic,
  boundary: number[][] = []
): __esri.Graphic[] => {
  const geometry = [];
  const graphicLine: __esri.Graphic[] = [];

  routes?.forEach((element: HVSRecord) => {
    const {
      longitude: elementLongitude = null,
      latitude: elementLatitude = null,
      deviceMessageSequenceNumber: sequenceNumber,
      eventTime,
      assetId,
      id,
      tripGroup,
      tripType
    } = element;
    geometry.push([
      elementLongitude,
      elementLatitude,
      eventTime,
      id,
      sequenceNumber,
      tripGroup,
      assetId,
      tripType
    ]);
  });

  geometry.forEach((node, index) => {
    // create a single line segment from point A to B
    // eventTime is at index 2 of each node.
    const idx = getItemIndex(geometry, index);
    if (!boundary?.length || isSequenceItem(node[4], routes, index)) {
      const id        = node[3] || node[6];
      const tripGroup = node[5] ?? null;
      const tripType  = `${id}-${node[7]}-${tripGroup}`;
      graphicLine.push(new Graphic({
        attributes : {eventTime: node[2], id, tripGroup, tripType},
        geometry   : createPolyLine({paths: [node, geometry[idx]]})
      }));
    }
  });
  return graphicLine;
};

/**
 * Defines a utility to validate if an item is in sequence order
 *
 * ```typescript
 * import {isSequenceItem} from '@ion/dls/src/maps/helpers'
 *
 * if (isSequenceItem(sequenceNumber, items, index)) {
 *   ....
 * }
 *
 * ```
 */
export const isSequenceItem = (sequenceNumber: number, items: HVSRecord[], index: number): boolean => {
  const idx    = getItemIndex(items, index);
  const nextSN = items[idx]?.deviceMessageSequenceNumber;
  return (sequenceNumber === nextSN || (sequenceNumber + 1) === nextSN);
};

/**
 * Defines a utility to return the index from array
 * return next index if exists, otherwise current one.
 *
 * ```typescript
 * import {getItemIndex} from '@ion/dls/src/maps/helpers'
 *
 * const index = getItemIndex(items, index)
 *
 * ```
 */
export const getItemIndex = (items: any[], index: number): number => {
  if (items?.length) {
    return items[index + 1] ? index + 1 : index;
  }
  return 0;
};

const createUniquePathRenderer = (assetsMap, colorIndexOf?: AssetColorIndexOf) => {
  const colorList = getColorList();
  return Object.entries(assetsMap).map(([key], i) => {
    return {
      value  : key,
      symbol : {
        type  : 'simple-line',
        color : getAssetColor({colorList, assetKey: key, position: i, colorIndexOf}),
        width : PATH_WIDTH,
        style : 'solid'
      }
    };
  });
};

/**
 * Creates a unique value renderer for Trip Services by assigning a distinct
 * color to each asset and generating one renderer entry per unique trip group.
 *
 * Supports assets represented as either:
 * - An object containing `nodes` and `routes`.
 * - A flat array of trip nodes.
 *
 * @param assetsMap A map of asset IDs to trip data.
 * @returns An array of unique value renderer definitions for trip groups.
 */
const createTripServicesRenderer = (assetsMap, colorIndexOf?: AssetColorIndexOf) => {
  const colorList = getColorList();

  // Build a color lookup keyed by assetId
  const assetColorMap = Object.fromEntries(
    Object.keys(assetsMap).map((assetId, i) => [
      assetId,
      getAssetColor({colorList, assetKey: assetId, position: i, colorIndexOf})
    ])
  );

  // Collect unique tripType and their parent assetId
  const seen = new Set();

  // Flatten all trips across all assets into uniqueValueInfos,
  // supporting assets with either { nodes, routes } or a flat array of nodes.
  return Object.entries(assetsMap).flatMap(([assetId, asset]) => {
    const nodes = Array.isArray(asset) ? asset : asset?.nodes ?? [];

    return nodes.reduce((acc, elem) => {
      const tripType = `${assetId}-${elem.tripType}-${elem.tripGroup}`;
      if (elem.tripType && !seen.has(tripType)) {
        seen.add(tripType);

        acc.push({
          value  : tripType,
          symbol : {
            type  : 'simple-line',
            color : assetColorMap[assetId],
            width : PATH_WIDTH,
            style : 'solid'
          }
        });
      }

      return acc;
    }, []);
  });
};

/**
 * pathRenderer provides a renderer option for the feature layer used
 * for paths. If color arg is provided, paths will be rendered with the color.
 *
 * ```typescript
 * import {createUniqueValuePathRenderer} from '@ion/dls/src/maps/helpers'
 * ...
 * const backgroundPathLayer = await createFeatureLayer(
 *  BACKGROUND_PATH_LAYER, pathGraphic, createUniqueValuePathRenderer(assetsMap)
 * );
 * ...
 *
 * ```
 */
export const createUniqueValuePathRenderer = (
  assetsMap,
  isTripServicesEnabled: boolean = false,
  colorIndexOf?: AssetColorIndexOf
) => {
  return {
    type             : 'unique-value',
    field            : !isTripServicesEnabled ? 'id' : 'tripType',
    uniqueValueInfos : !isTripServicesEnabled
      ? createUniquePathRenderer(assetsMap, colorIndexOf)
      : createTripServicesRenderer(assetsMap, colorIndexOf)
  };
};

/**
 * pathRenderer provides a renderer option for the feature layer used
 * for paths. If color arg is provided, paths will be rendered with the color.
 *
 * ```typescript
 * import {createPathRenderer} from '@ion/dls/src/maps/helpers'
 * ...
 * const backgroundPathLayer = await createFeatureLayer(BACKGROUND_PATH_LAYER, pathGraphic, createPathRenderer());
 * ...
 *
 * ```
 */
export const createPathRenderer = (color = null) => {
  const args = color? {color} : {};
  return {
    type   : 'simple',
    symbol : createSimpleLine(args)
  };
};

const createUniqueEndNodeRenderer = (
  assetsMap: any,
  isAssetHistory: boolean = false,
  {offset = false, colorIndexOf}: EndNodeRendererOptions = {}
) => {
  const colorList = getColorList();
  const nodeArray = Object.entries(assetsMap).map(([key], i) => {
    /**
     *  Modulo operator below is to repeat colors in an array based on the length of the "assetList" array.
     *  This ensures that the colors are evenly distributed across the assets,
     *  even if the number of assets exceeds the available colors
     */
    const colorValue      = getAssetColor({colorList, assetKey: key, position: i, colorIndexOf});
    const graphicMapEvent = graphicMap({fillColor: colorValue, isAssetHistory, offset});
    return [{
      value  : `${SymbolTypes.startOfTripEvent}_${key}`,
      symbol : graphicMapEvent.startOfTripEvent
    }, {
      value  : `${SymbolTypes.endOfTripEvent}_${key}`,
      symbol : graphicMapEvent.endOfTripEvent
    }];
  });
  return nodeArray.flat();
};

/**
 * createEndNodeRenderer provides a renderer option for the feature layer used
 * for endEventnodes.
 *
 * ```typescript
 * import {createEndNodeRenderer} from '@ion/dls/src/maps/helpers'
 * ...
 * const endEventNodeLayer = await createFeatureLayer(END_EVENT_NODE_LAYER, endEventNodes, createEndNodeRenderer());
 * ...
 *
 * ```
 */
export const createEndNodeRenderer = (
  assetsMap: any,
  isAssetHistory: boolean = false,
  offsetOrOptions: boolean | EndNodeRendererOptions = false
) => {
  const options = typeof offsetOrOptions === 'boolean' ? {offset: offsetOrOptions} : offsetOrOptions;
  return {
    type             : 'unique-value',
    field            : 'symbolType',
    uniqueValueInfos : createUniqueEndNodeRenderer(assetsMap, isAssetHistory, options)
  };
};

const createUniqNodeRenderer = ({
  inTrip,
  assetsMap,
  iconSize = ASSET_HISTORY_ICON_SIZE.smallSize,
  env = ENVIRONMENT.US,
  isAssetHistory = false,
  offset = false,
  colorIndexOf
}: CreateUniqNodeRendererProps) => {
  const colorList = getColorList();
  const version   = getAssetMarkerVersion(offset);
  const nodeArray = Object.entries(assetsMap).map(([key], i) => {
    /**
     *  Modulo operator below is to repeat colors in an array based on the length of the "assetList" array.
     *  This ensures that the colors are evenly distributed across the assets,
     *  even if the number of assets exceeds the available colors
     */
    const colorValue        = getAssetColor({colorList, assetKey: key, position: i, colorIndexOf});
    const graphicMapEvent   = graphicMap({fillColor: colorValue, isAssetHistory, iconSize, offset, env});
    const harshMarkerParams = isAssetHistory ? {opacity: inTrip ? 1 : 0.5} : {};
    return [{
      value  : `${SymbolTypes.basicInTripEvent}_${key}`,
      symbol : inTrip? graphicMapEvent.basicInTripEvent: graphicMapEvent.basicOutsideTripEvent
    }, {
      value  : `${SymbolTypes.basicOutsideTripEvent}_${key}`,
      symbol : graphicMapEvent.basicOutsideTripEvent
    }, {
      value  : `${SymbolTypes.nonDirectionalEvent}_${key}`,
      symbol : graphicMapEvent.nonDirectionalEvent
    }, {
      value  : `${SymbolTypes.startOfTripEvent}_${key}`,
      symbol : inTrip? graphicMapEvent.basicInTripEvent: graphicMapEvent.basicOutsideTripEvent
    }, {
      value  : `${SymbolTypes.endOfTripEvent}_${key}`,
      symbol : inTrip? graphicMapEvent.basicInTripEvent: graphicMapEvent.basicOutsideTripEvent
    }, {
      value  : `${SymbolTypes.harshEvent}_${key}`,
      symbol : getHarshEventMarker({...harshMarkerParams, version, env}, isAssetHistory)
    }];
  });
  return nodeArray.flat();
};

/**
 * Checks if the current zoom level falls within the NORMAL icon size threshold.
 * 
 * Returns `true` if zoom is less than or equal to `IconZoomThreshold.NORMAL`.
 */
const isNormalIconSize = zoom => zoom <= IconZoomThreshold.Normal;

/**
 * Checks if the current zoom level falls within the MEDIUM icon size threshold.
 * 
 * Returns `true` if zoom is less than or equal to `IconZoomThreshold.LARGE`.
 */
const isMediumIconSize = zoom => zoom <= IconZoomThreshold.Large;

/**
 * Determines the offset size of an asset history icon based on its current size.
 * If the icon size (as a percentage of the default small size) is less than or equal
 * to the medium threshold, it returns the "small" offset; otherwise, it returns "large".
 *
 * @param {number} size - The current size of the icon.
 * @returns {number} - The calculated offset size for the icon.
 */
const getOffsetIconSize = size =>
  getPercent(ASSET_HISTORY_ICON_SIZE.smallSize, size) <= IconSizeMultiplier.Medium * 100
    ? ICON_OFFSET_SIZE.small : ICON_OFFSET_SIZE.large;

/**
 * Checks whether an offset icon should be displayed based on the current zoom level.
 * Offset icons are shown only if the zoom level exceeds the "Normal" threshold.
 *
 * @param {number} zoom - The current zoom level.
 * @returns {boolean} - True if the offset icon should be displayed, false otherwise.
 */
export const hasOffsetIcon = zoom => zoom > IconZoomThreshold.Normal;

/**
 * Returns the icon size based on the current map zoom level.
 *
 * Zoom-based sizing rules:
 * - zoom <= IconZoomThreshold.NORMAL                           → normal size (100%)
 * - IconZoomThreshold.NORMAL > zoom <= IconZoomThreshold.LARGE → 140%
 * - zoom > IconZoomThreshold.LARGE                             → 180%
 * 
 * ```typescript
 * import {getIconSizeByZoom} from '@ion/dls/src/maps/helpers/MapViewUtils'
 * ...
 * const iconSize = getIconSizeByZoom(zoom);
 * 
 * ```
 */
export const getIconSizeByZoom = (zoom: number, baseSize = ASSET_HISTORY_ICON_SIZE.smallSize): number => {
  if (isNormalIconSize(zoom)) {
    return baseSize * IconSizeMultiplier.Normal;
  }

  if (isMediumIconSize(zoom)) {
    return baseSize * IconSizeMultiplier.Medium;
  }

  return baseSize * IconSizeMultiplier.Large;
};

/**
 * Returns the appropriate layer renderer based on the current map zoom level.
 *
 * The renderer is selected using predefined zoom thresholds:
 * - Uses the `Normal` renderer when zoom is less than or equal to `IconZoomThreshold.NORMAL`
 * - Uses the `Medium` renderer when zoom is greater than NORMAL and less than or equal to `IconZoomThreshold.LARGE`
 * - Uses the `Large` renderer when zoom is greater than `IconZoomThreshold.LARGE`
 *
 * This allows dynamic icon sizing as the user zooms in and out of the map.
 * 
 * ```typescript
 * import {getLayerRenderer} from '@ion/dls/src/maps/helpers/MapViewUtils'
 * ...
 * const renderer = getLayerRenderer(zoom, renderers);
 * 
 * ```
 */
export const getLayerRenderer = (zoom: number, renderer: RendererMapType) => {
  if (isNormalIconSize(zoom)) {
    return renderer.Normal;
  }

  if (isMediumIconSize(zoom)) {
    return renderer.Medium;
  }

  return renderer.Large;
};

/**
 * Returns the appropriate icon size label based on the current zoom level.
 *
 * Zoom-based sizing rules:
 * - If the zoom level corresponds to `Normal`, returns `IconSize.Normal`.
 * - If the zoom level corresponds to `Medium`, returns `IconSize.Medium`.
 * - Otherwise, returns `IconSize.Large`.
 *
 *  ```typescript
 * import {getIconSizeLabelByZoom} from '@ion/dls/src/maps/helpers/MapViewUtils'
 * ...
 * const iconSizeLabel = getIconSizeLabelByZoom(zoom);
 * 
 * ```
 */
export const getIconSizeLabelByZoom = (zoom: number) => {
  if (isNormalIconSize(zoom)) {
    return IconSize.Normal;
  }

  if (isMediumIconSize(zoom)) {
    return IconSize.Medium;
  }

  return IconSize.Large;
};

/**
 * createNodeRenderer provides a renderer option for the feature layer used
 * for nodes. If inTrip arg is false, all nodes will be shown as grey color.
 *
 * ```typescript
 * import {createNodeRenderer} from '@ion/dls/src/maps/helpers'
 * ...
 * const inTripNodeLayer = createFeatureLayer(IN_TRIP_NODE_LAYER, nodeGraphic, createNodeRenderer(true));
 * ...
 *
 * ```
 */
export const createNodeRenderer = ({
  inTrip,
  assetsMap,
  iconSize = ASSET_HISTORY_ICON_SIZE.smallSize,
  env = ENVIRONMENT.US,
  isAssetHistory = false,
  offset = false,
  colorIndexOf
}: CreateNodeRendererProps) => {
  return {
    type             : 'unique-value',
    field            : 'symbolType',
    uniqueValueInfos : createUniqNodeRenderer({inTrip, assetsMap, isAssetHistory, iconSize, offset, env, colorIndexOf}),
    visualVariables  : [
      {
        type         : 'rotation',
        field        : 'heading',
        rotatioNType : 'geographic'
      }
    ]
  };
};

/**
 * applyFilterToLayers applies filterOption to array of layers
 * if no option is provided, it will show all features on the layers
 *
 * ```typescript
 * import {applyFilterToLayers} from '@ion/dls/src/maps/helpers'
 * ...
 * applyFilterToLayers([layer1, layer2, layer3], `eventTime > 168500302`)
 *
 * ```
 */
export const applyFilterToLayers = ({
  view,
  layers,
  filterOption = '',
  isPathHidden = false
}) => {
  layers.forEach(layer => {
    view.whenLayerView(layer).then(lv => {
      lv.visible = !(lv.layer.id === IN_TRIP_PATH_LAYER && isPathHidden);
      (lv as any).filter = {
        where : filterOption
      };
    });
  });
};

/**
 * filterAssetsBy method filters an array of assets by given field name
 *
 * * ```typescript
 * import {filterAssetsBy} from '@ion/dls/src/maps/helpers'
 * ...
 * const assetsFiltered = filterAssetsBy( assetsHistory, selectedHistoryAsset, 'assetName');
 *
 * ```
 */
export const filterAssetsBy = (
  assets: CVSAssetType[],
  selectedAsset: CVSAssetType,
  field: string
): CVSAssetType[] => {
  const value = selectedAsset?.[field];
  return value ? assets.filter(asset => asset?.[field] === value) : assets;
};

interface CompareAssetMapGeometry {
  asset: {
    latitude: number,
    longitude: number
  },
  map: {
    latitude: number,
    longitude: number
  }
}

/**
 * createAssetsMap takes array of assetHistory and sort them into
 * a map by id
 * It takes array of previous assetHistory as prev param as optional.
 * This case is being used when live update is needed on the history layer.
 *
 *  ```typescript
 *
 * import {createAssetsMap} from '@ion/dls/src/maps/helpers'
 * ...
 * const assetMap = createAssetsMap(assets);
 * ...
 *
 * ```
 */
export const createAssetsMap = (assets, prev?, skipFilter = false): AssetsMap => {
  const map: AssetsMap = {};
  const eventList = getEventList();

  if (Array.isArray(prev) && Array.isArray(assets)) {
    const clonePrev = cloneDeep(prev);
    assets = clonePrev.concat(assets);
  };
  if (assets) {
    assets?.forEach(asset => {
      const {id} = asset;
      const iOnEventName = getEventName({
        source : eventList,
        from   : EventCodeValueType.eventCode,
        val    : asset.eventCode
      });
      const avlEvent = {...asset, iOnEventName};
      if (map[id]) {
        map[id].push(avlEvent);
      } else {
        map[id] = [avlEvent];
      }
    });
  }
  if (!skipFilter) {
    const filtered = Object.entries(map)
      .filter(([_, value]) => Array.isArray(value) && value.length >= 2)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

    return filtered;
  }
  
  return map;
};

/**
 * Defines a utility to add / update / Remove graphics to Feature Layer
 *
 *  ```typescript
 *
 * import {applyEditsToLayer} from '@ion/dls/src/maps/helpers'
 *
 * const params = {deleteFeatures: features};
 *
 * const zoomToFeatures = () => {
 *   assetLayer?.queryExtent().then(({count, extent}) => {
 *      count && view.goTo(extent?.expand(EXTENT_FACTOR), mapViewGoToOptions);
 *    });
 * };
 *
 * applyEditsToLayer(assetLayer, params, zoomToFeatures);
 *
 * ```
 */
export const applyEditsToLayer = (layer: __esri.FeatureLayer, params: object, callback?: Function) => {
  if (layer) {
    layer.applyEdits(params)
      .then(() => {
        callback?.();
      });
  }
};

/**
 * Defines a utility to Remove graphics from Feature Layer
 *
 *  ```typescript
 *
 * import {removeAllFeatures} from '@ion/dls/src/maps/helpers'
 *
 * removeAllFeatures(assetLayer);
 *
 * ```
 */
export const removeAllFeatures = async(layer: __esri.FeatureLayer) => {
  return layer?.queryFeatures().then(({features}) => {
    applyEditsToLayer(layer, {deleteFeatures: features});
  });
};

/**
 * Defines a utility to return feature object
 * based on the instanceof object
 *
 *  ```typescript
 *
 * import {getFeatures} from '@ion/dls/src/maps/helpers'
 *
 * const features = getFeatures(Device);
 *
 * ```
 */
export const getFeatures = (value: Device | CVSAssetType) => {
  return value instanceof Device? value.getDevice(): value;
};

// loads and / or returns a reference to the graphics modules ...
const loadGraphicsModules = async() => {
  if (!graphicsModules) {
    graphicsModules = await initializeEsriModules('esri/Graphic', 'esri/geometry/Point');
  }
  return graphicsModules;
};

/**
 * Defines a utility to create and return array of graphics
 *
 *  ```typescript
 *
 * import {createGraphics} from '@ion/dls/src/maps/helpers'
 *
 * const graphics = createGraphics(features);
 *
 * ```
 */
export const createGraphics = async(features: any, mapFields?: GenericObject[]) => {
  if (features?.length) {
    const graphics         = [];
    const [Graphic, Point] = await loadGraphicsModules();
    features?.forEach((featureAsDevices: Device) => {
      const feature   = esriTypeCast(getFeatures(featureAsDevices), mapFields);
      const longitude = feature?.longitude ?? feature?.geometry?.y;
      const latitude  = feature?.latitude ?? feature?.geometry?.x;
      if (!latitude && !longitude) {
        feature.heading = 0;
      }
      const point     = new Point([longitude, latitude], {wkid: 4326});
      const graphic   = new Graphic(point, null, feature);
      graphics.push(graphic);
    });
    return graphics;
  }
  return [];
};

/**
 * Defines a utility to typecast the given attributes as per defined
 *
 *  ```typescript
 *
 * import {esriTypeCast} from '@ion/dls/src/maps/helpers'
 *
 * const converted = esriTypeCast(features);
 *
 * ```
 */
export const esriTypeCast = (feature: GenericObject, mapFields?: GenericObject[]) => {
  const modified = {};
  const fields   = idMap(mapFields || [], 'name');
  Object.entries(feature).forEach(([key, value]) => {
    if (
      isboolean(value) ||
      (!isNil(value) && fields[key]?.type === 'string' && !isString(value))
    ) {
      modified[key] = value.toString();
    } else if (isUndefined(value)) {
      modified[key] = null;
    } else {
      modified[key] = value;
    }
  });
  mapFields?.map(({name}) => {
    if (isUndefined(modified[name])) {
      modified[name] = null;
    }
  });
  return modified;
};

export const createSearchPoint = async(latitude: number, longitude: number, selectedLocation: SavedPlacesProps) => {
  const [Graphic] = await initializeEsriModules('esri/Graphic');
  const point = {
    type : 'point',
    longitude,
    latitude
  };
  const pointGraphic = new Graphic({
    geometry   : point,
    attributes : selectedLocation,
    symbol     : {
      type   : 'picture-marker',
      url    : getLocationMarker(),
      width  : 24,
      height : 24
    }
  });

  return pointGraphic;
};

/**
 * Defines a utility to get data updates based on latest graphics time
 *
 *  ```typescript
 *
 * import {loadDataUpdates} from '@ion/dls/src/maps/helpers'
 *
 * loadDataUpdates(scDeviceLayer, 'db_write_time', callback);
 *
 * ```
 */
export const loadDataUpdates = (scDeviceLayer: __esri.FeatureLayer, field: string, callback: Function) => {
  if (scDeviceLayer) {
    scDeviceLayer.queryFeatures().then(({features}) => {
      const dates = features?.map(({attributes}) => {
        const propertyValue = attributes[field];
        if (propertyValue) {
          return new Date(propertyValue)?.getTime();
        }
        return null;
      });
      if (dates?.length) {
        const recent = Math.max(...dates);
        if (!isNaN(recent)) {
          callback(new Date(recent).toISOString());
        } else {
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  } else {
    callback(null);
  }
};

/**
 * Defines a utility to show SC Device Popup
 *
 *  ```typescript
 *
 * import {showSCDevicePopup} from '@ion/dls/src/maps/helpers'
 *
 * showSCDevicePopup(params);
 *
 * ```
 */
export const showSCDevicePopup = ({
  view,
  crumbs,
  isPhone,
  setBreadCrumbs,
  selectedGraphic,
  setSelectedDevice,
  setDeviceDetailView,
  isHistoryAccessible,
  setFindButtonStatus,
  assetFindNearbyClick,
  isFindNearbyPopup,
  onFindNearyBy,
  onFindNearbyPopupStatusChange,
  onFindNearbyBreadcrumbBackClick,
  onFindButtonStateChange,
  findLabel,
  onDeviceReports
}: SCDevicePopupProps) => {
  if (!isPhone && selectedGraphic) {
    const {attributes, geometry} = selectedGraphic;
    const container = RootManager.createContainer('sci-device-popup');
    roots.render(
      <SCDevicePopup selectedDevice={attributes}
        crumbs={crumbs}
        setCrumbs={setBreadCrumbs}
        setSelectedDevice={setSelectedDevice}
        setDeviceDetailView={setDeviceDetailView}
        setFindButtonStatus={setFindButtonStatus}
        isHistoryAccessible={isHistoryAccessible}
        isFindNearbyPopup={isFindNearbyPopup}
        onFindNearyBy={onFindNearyBy}
        onFindNearbyBreadcrumbBackClick={onFindNearbyBreadcrumbBackClick}
        onFindNearbyPopupStatusChange={onFindNearbyPopupStatusChange}
        onFindButtonStateChange={onFindButtonStateChange}
        assetFindNearbyClick={assetFindNearbyClick}
        findLabel={findLabel}
        onDeviceReports={onDeviceReports}/>,
      container
    );
    const {longitude, latitude} = attributes;
    view.popup.open({
      location : {
        type : 'point',
        longitude,
        latitude
      },
      content : container
    });
  }
};

/**
 * Defines a utility to DeviceStatus
 *
 *  ```typescript
 *
 * import {getDeviceStatus} from '@ion/dls/src/maps/helpers'
 *
 * const status = getDeviceStatus(state);
 *
 * ```
 */

export const getDeviceStatus = (state: boolean, device?: BaseAsset) => {
  const {longitude, latitude} = device || {};
  if (!isNil(device) && !isNonZeroNumber(longitude) && !isNonZeroNumber(latitude)) {
    return translate('common:assetState.NoGPS');
  }
  const status = !isNil(state) ? (isTrue(state) ? 'moving' : 'stationary') : 'unknown';
  return translate(`common:sciStatus.${status}`);
};

const VIEW_HIT_TEST_METHOD = {
  [VERSION.V1] : hitTest,
  [VERSION.V2] : hitTestV2
};

/**
 * Provides a utility to catch the event and determine
 * if the click is for selects a graphic or just clicks on the view
 */
export const onLayerClickEvent = ({
  x,
  y,
  view,
  layerId,
  callback,
  includeAny = true,
  includeResults = true,
  hitTestVersion = VERSION.V1
}: LayerClickEvent) => {
  const method = VIEW_HIT_TEST_METHOD[hitTestVersion];
  method({x, y}, {
    view,
    layerId,
    includeAny,
    includeResults,
    handler : (attributes, graphic) => {
      graphic && attributes && callback(graphic, true);
    }
  });
};

/**
 * Provides a convenience function which resolves a selected saved search location
 * by returning the result directly if it has previously been retrieved, or retrieving
 * the result if not.
 */
export const retrieveLocation = async(result: SavedPlacesProps): Promise<SavedPlacesProps> => {
  if (!result.longitude && !result.latitude) {
    const results = await searchMapAddressCandidates({
      magicKey   : result.magicKey,
      singleLine : result.text
    });
    const {location, address} = results.data?.candidates?.[0];
    return {
      magicKey  : result.magicKey,
      longitude : location.x,
      latitude  : location.y,
      text      : address
    };
  }
  return result as SavedPlacesProps;
};

/**
 * Provides a convenience function which resolves a selected saved search location
 * by returning the HERE result directly if it has previously been retrieved, or retrieving
 * the result if not.
 *
 * ```typescript
 *
 *  import {retrieveHereLocation} from '@ion/dls/src/maps/helpers';
 *
 *  const suggestions = await retrieveHereLocation(location);
 *
 * ```
 */
export const retrieveHereLocation = async(result: SavedPlacesProps, useLatestVersionAPI = false): Promise<any> => {
  if (!result.longitude && !result.latitude && !result.position) {
    const results = await getMapAddressCandidates(result.locationId || result.id, useLatestVersionAPI);
    const {Address: {Label}, DisplayPosition} = results.data?.Response.View[0].Result[0].Location || {};
    const {position, title, id} = results.data?.items || {};
    const {Latitude, Longitude} = DisplayPosition || {};
    const {lat, lng} = position || {};
    return {
      locationId : useLatestVersionAPI ? id : result.locationId,
      longitude  : useLatestVersionAPI ? lng : Longitude,
      latitude   : useLatestVersionAPI ? lat : Latitude,
      label      : useLatestVersionAPI ? title : Label
    };
  } else if (result.position) {
    const {title, id, position} = result;
    const {lat, lng} = position;
    return {
      locationId : id,
      longitude  : lng,
      latitude   : lat,
      label      : title
    };
  }
  return result as SavedPlacesProps;
};

/**
 * The function which resolves search location details of the searched location
 * by either Esri or HERE services. The function itself find the service based on
 * the search result properties.
 *
 * ```typescript
 *
 *  import {getLocations} from '@ion/dls/src/maps/helpers';
 *
 *  const suggestions = await getLocations(location);
 *
 * ```
 */
export const getLocations = async({
  searchText,
  cancelToken,
  mapView,
  useExtent,
  useHereApi = false
}: GetLocationsParams) => {
  const {latitude, longitude} = mapView.center;
  const param: any = useHereApi ? {
    text       : searchText,
    maxResults : 4,
    center     : [latitude, longitude],
    cancelToken
  } : {
    text         : searchText,
    searchExtent : getSearchExtentParams(mapView, useExtent),
    cancelToken
  };
  const api     = useHereApi ? suggestMapSearch : searchMapSuggest;
  const results = await api(param);
  return results.data.suggestions;
};

/**
 * The function used to get the location details of the specified searched location
 * using either the Esri API or the HERE API based on the search location  properties.
 *
 * ```typescript
 *
 *  import {getLocationDetails} from '@ion/dls/src/maps/helpers';
 *
 *  const details = await getLocationDetails(location);
 *
 * ```
 */
export const getLocationDetails = async(location: SavedPlacesProps) => {
  let locationDetail = null;
  if (location.magicKey && location.text) {
    const {
      longitude: lat,
      latitude: lng,
      text
    } = await retrieveLocation(location);
    locationDetail = {
      magicKey  : location.magicKey,
      longitude : lat,
      latitude  : lng,
      label     : location.text?.split?.(',')[0],
      text
    };
  } else if ((location.locationId || location.id) && (location.label || location.title)) {
    const {
      longitude: lat,
      latitude: lng,
      label,
      locationId
    } = await retrieveHereLocation(location, !!location.id);
    locationDetail = {
      locationId : locationId,
      longitude  : lat,
      latitude   : lng,
      text       : label,
      label      : label?.split?.(',')?.[0]
    };
  }
  return locationDetail;
};

/**
 * The function is used to get the location label from the full location detail text.
 *
 * ```typescript
 *
 *  import {getLocationlabel} from '@ion/dls/src/maps/helpers'
 *
 *  const label = getLocationlabel(location, true);
 *
 * ```
 *
 * @param location   The location text or label
 * @param useHereApi flag tells whether the result is from HERE or Esri
 * @returns display location label
 */
export const getLocationlabel = (location: string, useHereApi = false): string => {
  if (location) {
    const searchSplits = location.split(',');
    return (searchSplits[0] || location).trim();
  }
};

/**
 * Defines a utility to verify selected asset updated
 *
 *  ```typescript
 *
 * import {hasAssetUpdated} from '@ion/dls/src/maps/helpers'
 *
 * const selectedAssetUpdated = hasAssetUpdated(previous, current);
 *
 * ```
 */
export const hasAssetUpdated = (previous: CVSAssetType, current: CVSAssetType): boolean => {
  if (previous && current) {
    return current.assetId === previous.assetId && current.messageUuid !== previous.messageUuid;
  }
  return false;
};

/**
 * Defines a utility to return the view's min zoom level constraint
 *
 *  ```typescript
 *
 * import {getViewMinZoomLevel} from '@ion/dls/src/maps/helpers'
 *
 * const minZoomLevel = getViewMinZoomLevel(true);
 *
 * ```
 */
export const getViewMinZoomLevel = (isPhone: boolean): number => (
  ViewMinZoomLevel[isPhone ? 'phone' : 'desktop']
);

/**
 * Defines a utility to return the latest update for given item based on updated list
 *
 *  ```typescript
 *
 * import {getLatestUpdate} from '@ion/dls/src/maps/helpers'
 *
 * const latestUpdate = getLatestUpdate(params);
 *
 * ```
 */
export const getLatestUpdate = ({
  list,
  item,
  key = 'assetId',
  prop = 'eventTime'
}: LatestUpdateParams) => {
  if (list && item) {
    const val     = item[key];
    const updated = list?.find(entry => entry[key] === val);
    return updated?.[prop] !== item?.[prop] ? updated : item;
  }
  return item;
};

export const createEventType = (type: SymbolTypes, id: number): string => `${type}_${id}`;

export const isEventType = (type: SymbolTypes, {attributes: {id, symbolType}}): boolean => {
  return createEventType(type, id) === symbolType;
};

/**
 * Defines a utility to return the Asset Layer Renderer
 *
 *  ```typescript
 *
 * import {getAssetLayerRenderer} from '@ion/dls/src/maps/helpers'
 *
 * const renderer = getAssetLayerRenderer();
 *
 * ```
 */
export const getAssetLayerRenderer = () => {
  return {
    type   : 'simple',
    symbol : {
      type : 'cim',
      data : {
        type               : 'CIMSymbolReference',
        primitiveOverrides : [{
          type                : 'CIMPrimitiveOverride',
          primitiveName       : 'arrowRotation',
          propertyName        : 'Rotation',
          valueExpressionInfo : {
            type       : 'CIMExpressionInfo',
            title      : 'Custom Rotation',
            expression : '$feature.heading',
            returnType : 'Default'
          }
        }, {
          type                : 'CIMPrimitiveOverride',
          primitiveName       : 'arrowColor',
          propertyName        : 'Color',
          valueExpressionInfo : {
            type       : 'CIMExpressionInfo',
            title      : 'Custom color',
            expression : '$feature.utilizationColor',
            returnType : 'String'
          }
        }],
        symbol : PointSymbol.createPointMarker({
          arrowColor        : [105, 178, 70, 255],
          arrowCircleColor  : [249, 249, 249],
          isSelected        : false,
          endIcon           : null,
          title             : null,
          heading           : 141,
          markerOnly        : true,
          isLabellingActive : false,
          assetType         : null
        })
      }
    }
  };
};

enum AssetStateColor {
  ACTIVE = '#41CA00',
  MOVING = '#41CA00',
  IDLE = '#FFB800',
  PARKED = '#A59D89',
  NO_MOVING = '#FF965A',
  NO_GPS = '#FF965A'
};

/**
 * Generates a client-side unique-value renderer for asset features.
 *
 * The renderer uses a value expression to determine asset status based on:
 * - GPS location (latitude/longitude),
 * - GPS speed,
 * - Ignition status,
 * - Asset type (Machine, Powered, Unpowered).
 *
 * It categorizes each asset into one of the following statuses:
 * - NOGPS: asset has no GPS signal (lat/lon == 0)
 * - ACTIVE: machine with ignition on
 * - MOVING: powered asset with ignition on and moving
 * - IDLE: powered asset with ignition on but not moving
 * - TOWED: asset is moving but ignition is off
 * - PARKED: asset is stationary and ignition is off
 *
 * Each status is mapped to a specific picture marker symbol.
 * Symbols are rotated using the `heading` attribute with geographic rotation.
 */
export const createClientAssetRenderer = () => {
  return {
    type            : 'unique-value',
    valueExpression : `if (($feature.latitude == 0) && ($feature.longitude == 0)) {
        return 'NOGPS'
    }

    if (IsEmpty($feature.gpsSpeed)) {
        return ''
    }

    if ($feature.assetType == 'Machine') {
        if($feature.ignition == 'true') {
            return 'ACTIVE'
        } else {
          IF ($feature.gpsSpeed > 1) { 
                return 'TOWED'
          } else {
                return 'PARKED'
          }
        }
    }

    if ($feature.assetType == 'Powered') {
        if($feature.ignition == 'true') {
            if ($feature.gpsSpeed > 1) { 
                return 'MOVING'
            } else {
                return 'IDLE'
            }
        } else {
            if ($feature.gpsSpeed > 1) { 
                return 'TOWED'
            } else {
                return 'PARKED'
            }
        }
    }
        
    if ($feature.assetType == 'Unpowered') {
        if ($feature.gpsSpeed > 1) { 
            return 'TOWED'
        } else {
            return 'PARKED'
        }
    }

    if ($feature.ignition == 'true') {
        if ($feature.gpsSpeed > 1) { 
            return 'MOVING'
        } else {
            return 'IDLE'
        }
    } else {
        if ($feature.gpsSpeed > 1) { 
            return 'TOWED'
        } else {
            return 'PARKED'
        }
    }`,
    valueExpressionTitle : 'Custom',
    defaultLabel         : 'Other',
    defaultSymbol        : {
      type   : 'picture-marker',
      url    : getAssetEventStatusMarker({fillColor: AssetStateColor.PARKED}),
      width  : '28px',
      height : '28px'
    },
    visualVariables : [{
      type         : 'rotation',
      field        : 'heading',
      rotationType : 'geographic'
    }],
    uniqueValueInfos : [
      {
        value  : 'NOGPS',
        label  : 'NO GPS',
        symbol : {
          type   : 'picture-marker',
          url    : getAssetEventNoGPSMarker({fillColor: AssetStateColor.NO_GPS}),
          width  : '28px',
          height : '28px'
        }
      },
      {
        value  : 'TOWED',
        label  : 'TOWED',
        symbol : {
          type   : 'picture-marker',
          url    : getAssetEventStatusMarker({fillColor: AssetStateColor.NO_MOVING}),
          width  : '28px',
          height : '28px'
        }
      },
      {
        value  : 'PARKED',
        label  : 'PARKED',
        symbol : {
          type   : 'picture-marker',
          url    : getAssetEventStatusMarker({fillColor: AssetStateColor.PARKED}),
          width  : '28px',
          height : '28px'
        }
      },
      {
        value  : 'MOVING',
        label  : 'MOVING',
        symbol : {
          type   : 'picture-marker',
          url    : getAssetEventStatusMarker({fillColor: AssetStateColor.MOVING}),
          width  : '28px',
          height : '28px'
        }
      },
      {
        value  : 'IDLE',
        label  : 'IDLE',
        symbol : {
          type   : 'picture-marker',
          url    : getAssetEventStatusMarker({fillColor: AssetStateColor.IDLE}),
          width  : '28px',
          height : '28px'
        }
      },
      {
        value  : 'ACTIVE',
        label  : 'ACTIVE',
        symbol : {
          type   : 'picture-marker',
          url    : getAssetEventStatusMarker({fillColor: AssetStateColor.ACTIVE}),
          width  : '28px',
          height : '28px'
        }
      }
    ]
  };
};

/**
 * The applyDynamicLatLngStyling function customizes the rendering of a feature layer
 * by applying dynamic styles based on geographic properties (latitude and longitude)
 * and cluster attributes. It adjusts the color of features based on their average
 * latitude, longitude, and cluster count, allowing for a visual differentiation of
 * features in a map based on these metrics.
 *
 *  ```typescript
 *
 *  import {applyDynamicLatLngStyling} from '@ion/dls/src/maps/helpers'
 *
 *  const renderer = applyDynamicLatLngStyling(cvsLayer?.renderer, clusterColor);
 *
 * ```
 *
 * @param existingRenderer - The existing renderer to apply the styling to. If not provided,
 *                           a new renderer will be created.
 * @param clusterColor - The current color mode that affects the rendering.
 * @returns The updated renderer with dynamic styling applied.
 */
export const applyDynamicLatLngStyling = (externalRenderer: any, clusterColor: ColorModeEnum) => {
  const renderer = externalRenderer || getAssetLayerRenderer();
  const visualVariables = renderer?.visualVariables;
  renderer.visualVariables = [
    ...(isArray(visualVariables) ? visualVariables : []), {
      type  : 'color',
      field : 'avg_latitude',
      stops : [
        // Adding colors for cluster based on the value
        {value: -1, color: 'default'},
        {value: 0, color: dlsColor('caOrange')},
        {value: 1, color: dlsColor(ASSET_CLUSTER.small.color)},
        {value: 26.25, color: dlsColor(ASSET_CLUSTER.medium.color)},
        {value: 48.75, color: dlsColor(ASSET_CLUSTER.xMedium.color)},
        {value: 75, color: dlsColor(ASSET_CLUSTER.large.color)},
        {value: 115.5, color: dlsColor(ASSET_CLUSTER.xLarge.color)}
      ],
      valueExpression : `
          var lat   = IIf(HasKey($feature, 'avg_latitude'), $feature.avg_latitude, 0);
          var lon   = IIf(HasKey($feature, 'avg_longitude'), $feature.avg_longitude, 0);
          var count = IIf(HasKey($feature, 'cluster_count'), $feature.cluster_count, 1);
          if (count == 1) {
            return -1;
          } else if (lat == 0 && lon == 0) {
            return 0;
          } else {
            return count;
          }
        `
    }
  ];
  return renderer;
};

/**
 * Defines a utility to return the color of asset Utilization
 *
 *  ```typescript
 *
 * import {getUtilizationColor} from '@ion/dls/src/maps/helpers'
 *
 * const color = getUtilizationColor();
 *
 * ```
 */
export const getUtilizationColor = (asset: CVSAssetType) => {
  const color = MarkerArrowColor[getAssetState(asset as any)];
  return dlsColor(color ?? 'grey3');
};

// Defines a utility to get latest time from a list based on a field and call a callback
export const getItemsUpdates = (
  items: CVSAssetType[],
  field: string = 'dbWriteTime',
  callback: (timestamp: string | null) => void
) => {
  if (callback) {
    const lastTime = getLatestTime(items as any, field);
    const time = lastTime ? new Date(lastTime).toISOString() : null;
    callback(time);
  }
};

/**
 * Defines a utility to returns a layer based on title param
 *
 *  ```typescript
 *
 * import {getLayerByTitle} from '@ion/dls/src/maps/helpers'
 *
 * const layer = getLayerByTitle(layers, title);
 *
 * ```
 */
export const getLayerByTitle = (layers: __esri.Layer[], layerTitle: string) => {
  return layers?.items?.find(({title}) => title === layerTitle);
};

/**
 * Defines the utility for status icon in SC Device popup.
 * ```typescript
 *
 *  import {getSCStatus} from '@ion/dls/src/maps/helpers';
 *  const scStatusIcon = getSCStatus(status);
 *
 * ```
 */
export const getSCStatus = (status: string, device?: BaseAsset) => {
  const {longitude, latitude} = device || {};
  if (!isNil(device) && !isNonZeroNumber(longitude) && !isNonZeroNumber(latitude)) {
    return AssetState.NOGPS;
  }
  return !isNil(status) ? (isTrue(status) ? AssetState.MOVING : AssetState.PARKED) : AssetState.PARKED;
};

/**
 * Defines a utility to return a query Layer Effect.
 * ```typescript
 *
 *  import {getLayerEffectQuery} from '@ion/dls/src/maps/helpers';
 *  const query = getLayerEffectQuery({selectedItem, property, ...});
 *
 * ```
 */
export const getLayerEffectQuery = ({
  defaultQuery = noneSelectedQuery,
  selectedItem,
  type,
  query
}: LayerEffectQueryParams) => {
  return selectedItem?.[type] ? query : defaultQuery;
};

/**
 * Defines a utility to return a query from item (Asset, Device,..).
 * ```typescript
 *
 *  import {getItemQuery} from '@ion/dls/src/maps/helpers';
 *  const query = getItemQuery({item, property, ...});
 *
 * ```
 */
export const getItemQuery = ({
  item,
  property,
  defaultQuery
}: ItemQueryParams) => {
  if (item) {
    const value = item[property] || null;
    return value ? `${property} = ${value}` : defaultQuery;
  }
  return null;
};

/**
 * Defines a utility to return MapView Zoom Params
 *
 * ```typescript
 *
 *  import {getMapviewZoomParams} from '@ion/dls/src/maps/helpers';
 *  const params = getMapviewZoomParams(extent, count, zoomLevel);
 *
 * ```
 */
export const getMapviewZoomParams = (extent: __esri.Extent, count: number, zoomLevel: number) => {
  if (extent) {
    if (count === ASSET_SINGLE_COUNT && zoomLevel) {
      return {extent, zoom: zoomLevel};
    }
  };
  return extent;
};

/**
 * Defines a utility to return Default Saved MapView
 *
 * ```typescript
 *
 *  import {getDefaultSavedMapView} from '@ion/dls/src/maps/helpers';
 *  const defaultSavedMapView = getDefaultSavedMapView(savedMapViews);
 *
 * ```
 */
export const getDefaultSavedMapView = (savedMapViews: SavedMapViewType[]) => {
  const defaultMapView = savedMapViews?.find(({isDefault}) => isDefault);
  return defaultMapView || null;
};

/**
 * Defines a utility to return if there is a Tracking Filter Active
 *
 * ```typescript
 *
 *  import {isTrackingFilterActive} from '@ion/dls/src/maps/helpers';
 *  const isTrackingFilterActive = isTrackingFilterActive(isFilterActive, savedFilters);
 *
 * ```
 */
export const isTrackingFilterActive = (isFilterActive: boolean, savedFilters: string) => {
  return isFilterActive || !isNil(savedFilters);
};

/**
 * Provides the viewHitTest implementation for all map gestures, invoking the given
 * handler when the event occurs on an item within the layer of interest.
 *
 * ```typescript
 *   const onSelect = (view, layerId, handler) => {
 *     view.on('click', function({x, y}) {
 *       viewHitTest({x, y}, {view, options, handler});
 *     });
 *   };
 *
 *   view.on('click', function({x, y}) {
 *     viewHitTest({x, y}, {
 *        view,
 *        options : ['some-layer'],
 *        handler : (attributes}) => {
 *         ...
 *        }
 *      });
 *   });
 *
 * ```
 */
export const viewHitTest = ({x, y}: Coordinates, {
  view,
  options,
  handler,
  layerView,
  zoomHandler
}: HitTestParms) => {
  view.hitTest({x, y}, options).then(({results}) => {
    const {result, graphic} = individualFeature({handler, results});
    if (!result && graphic && layerView && zoomHandler) {
      zoomHandler({view, layerView, graphic});
    }
  });
};

/**
 * Defines a utility to find the individual feature selected and passing
 * it on the handler method.
 */
const individualFeature = ({handler, results}: IndividualFeature) => {
  let result      = null;
  const {graphic} = results[0] || {};
  if (!graphic?.isAggregate) {
    result = graphic?.attributes;
  }
  handler(result);
  return {result, graphic};
};

/**
 * Defines a utility to zoom to cluster extent
 *
 * ```typescript
 *
 *  import {zoomToClusterExtent} from '@ion/dls/src/maps/helpers/MapViewUtils';
 *  zoomToClusterExtent({view, layerView, graphic})
 *
 * ```
 */
export const zoomToClusterExtent = ({view, layerView, graphic}: ZoomToClusterExtent) => {
  if (graphic?.isAggregate) {
    const query        = layerView.createQuery();
    query.aggregateIds = [graphic.getObjectId()];
    layerView.queryFeatures(query).then(({features}) => {
      if (features?.length) {
        const query = layerView.createQuery();
        query.where = `assetId IN (${features.map(({attributes}) => attributes?.assetId)})`;
        layerView.layer.queryExtent(query).then(({extent}) => {
          extent && view.goTo(extent?.expand(EXTENT_FACTOR), mapViewGoToOptions);
        });
      }
    });
  }
};

// Defines label class props
type LabelClassProps = {
  labelContent: string,
  selectedAsset: CVSAssetType,
  isCVSRenderer: boolean,
  identifierType: string,
  identifierProperty: string,
  defaultColor: boolean,
  showAllLabels?: boolean,
  basemap: BaseMapEnum
}

/**
 * Defines a utility to return a label class based on the given parameters.
 *
 * ```typescript
 *
 *  import {getLabelClass} from '@ion/dls/src/maps/helpers';
 *  const labelClass = getLabelClass(params);
 *
 * ```
 */
export const getLabelClass = ({
  labelContent,
  selectedAsset,
  isCVSRenderer,
  identifierType,
  identifierProperty,
  defaultColor,
  showAllLabels,
  basemap
}: LabelClassProps) => {
  const where = !showAllLabels ? getLabelClassQuery({
    selectedItem : selectedAsset,
    type         : identifierType,
    property     : identifierProperty
  }) : null;
  const labelClass = getLayerLabelClass({
    isRenderer : isCVSRenderer,
    defaultColor,
    labelContent,
    basemap,
    where
  });
  return labelClass;
};

/**
 * isRouteMatchingEnabled method helps to validate if Route Matching is enabled based on
 * a validation.
 *
 * ```typescript
 * import {isRouteMatchingEnabled} from '@ion/dls/src/maps/helpers/MapViewUtils';
 *
 * ...
 *
 * ```
 *
 */
export const isRouteMatchingEnabled = (count: number) => {
  return count > 0 && count <= parseInt(get('maxHVSRouteMatchingAssetCount'));
};

// Defines how many break levels will be generated for clustering purposes
const CLUSTER_STEPS = 3;

/**
 * Generates size and color breaks for cluster visualization based on the point count.
 * Each break corresponds to a predefined asset cluster style (size & color).
 *
 * @param {Array} clusterCount - Array of cluster count.
 * @returns {Array} Array of break objects with min, max, size, and color.
 */
const generateClusterSizeBreaks = clusterCount => {
  const clusterLength = clusterCount?.length;
  if (!clusterLength) return [];

  const breaks      = [];
  const clusterKeys = Object.keys(ASSET_CLUSTER);

  // Determine the minimum and maximum values from the cluster counts
  const min            = Math.min(...clusterCount);
  const max            = Math.max(...clusterCount);
  // Prevent division by zero
  const range          = max - min || 1;
  // Size of each break range
  const stepSize       = range / CLUSTER_STEPS;
  // Fallback key for styles
  const lastClusterKey = clusterKeys[clusterKeys.length - 1];

  for (let i = 0; i < CLUSTER_STEPS; i++) {
    // Select style key for size
    const key     = clusterKeys[i] || lastClusterKey;
    // Lower bound of current break
    const minVal  = Math.round(min + i * stepSize);
    // Upper bound of break
    const maxVal  = Math.round(i === CLUSTER_STEPS - 1 ? max : min + (i + 1) * stepSize);
    // Choose the key for color (possibly offset to vary from size key)
    const nextKey = clusterKeys[i === 0 ? i : i * 2];

    breaks.push({
      // Avoid overlap between ranges
      min   : i === 0 ? minVal : minVal + 1,
      max   : maxVal,
      size  : ASSET_CLUSTER[key].size,
      color : ASSET_CLUSTER[nextKey].color
    });
  }
  return breaks;
};

/**
 * Constructs an array of asset clusters with size and color based on point counts.
 * @param {Array} clusters - The input array of cluster elements.
 * @param {boolean} isClusterMarkerV2 - The flag to include size and color in cluster
 * @returns {Array} - Processed array of asset clusters with size and color.
 */
export const constructClusters = (clusters: any[], isClusterMarkerV2: boolean = false) => {
  if (!clusters?.length) return [];

  if (isClusterMarkerV2) {
    const clusterUpdated = [];
    const clusterCount   = clusters.map(({properties}) => properties?.point_count);
    const clusterSizes   = generateClusterSizeBreaks(clusterCount);

    clusters.forEach(element => {
      const {point_count} = element.properties;
      // Find the matching size/color for the point_count
      const matchedCluster = clusterSizes.find(el => point_count >= el.min && point_count <= el.max);

      if (matchedCluster) {
        const {size, color} = matchedCluster;
        clusterUpdated.push({
          ...element,
          size,
          color
        });
      }
    });
    return clusterUpdated;
  }
  return clusters;
};
/**
 * Returns the top offset value for the map widget based on the tracking filter
 * and whether the widget is a geofence widget.
 *
 * @param hasTrackingFilterActive - Indicates if the tracking filter is active.
 * @param isGeofenceWidget - Indicates if the widget is a geofence widget.
 * @returns The appropriate top offset value for the map widget.
 */
export const getMapWidgetTopOffset = (
  hasTrackingFilterActive: boolean = false,
  isGeofenceWidget: boolean = false
): number => {
  if (isGeofenceWidget) {
    return hasTrackingFilterActive
      ? MAP_GEOFENCE_WIDGET_POSITION.topWithFilter
      : MAP_GEOFENCE_WIDGET_POSITION.topWithOutFilter;
  }

  return hasTrackingFilterActive
    ? MAP_WIDGET_POSITION.topWithFilter
    : MAP_WIDGET_POSITION.topWithOutFilter;
};

type ArrayOrObject = any[] | GenericObject;

/**
 * Merges two values if they are both arrays or both plain objects.
 * - If both inputs are arrays, returns a new array containing elements from both.
 * - If both inputs are objects, returns a new object combining their properties.
 * - If the types do not match, an error is thrown.
 *
 * @param data1 - First array or object to merge
 * @param data2 - Second array or object to merge
 * @returns A merged array or object, depending on the input types
 * @throws Error if inputs are not of the same type (both arrays or both objects)
 */
export const mergeArrayOrObject = (data1: ArrayOrObject, data2: ArrayOrObject): any => {
  if (Array.isArray(data1) && Array.isArray(data2)) {
    return [...data1, ...data2];
  }
  return {...data1, ...data2};
};

/**
 * Splits an Data into smaller arrays (batches) of a specified maximum size.
 *
 * @param {Array} data - The data array to be divided into batches.
 * @param {number} batchSize - The maximum size of each batch.
 * @returns {Array[]} An array containing the batched subarrays.
 */
export const splitDataIntoBatches = (data: any[], batchSize: number) => {
  if (Array.isArray(data) && Number(batchSize) && batchSize > 0) {
    const result = [];

    for (let i = 0; i < data.length; i += batchSize) {
      result.push(data.slice(i, i + batchSize));
    }

    return result;
  }
  return data;
};

/**
 * Builds a combined feature filter query.
 * 
 * Behavior:
 * - If both `featureFilter.where` and `query` exist, they are combined using `AND`.
 * - If only `featureFilter.where` exists, it is returned as-is.
 * - If only `query` exists, it is returned.
 *
 * @param query - The new query condition to apply.
 * @param featureFilter - An object that may contain an existing `where` clause.
 * @returns A combined query string.
 */
export const getFeatureFilter = (query: string, featureFilter: GenericObject) => {
  const whereClause = featureFilter?.where;

  if (whereClause && query?.length) {
    return `${whereClause} AND ${query}`;
  }

  return whereClause || query;
};

/**
 * Returns the appropriate user unit of measurement (UOM)
 * based on the provided system of units.
 *
 * @param {string} systemOfUnits - The system of units to evaluate (e.g., "Metric" or "Imperial").
 * @returns {Unit} The corresponding unit of measurement (Unit.METRIC or Unit.IMPERIAL).
 * 
 * ```typescript
 * import {getUserUOM} from '@ion/dls/src/maps/helpers/MapViewUtils';
 *
 * ...
 *
 * ```
 */
export const getUserUOM = systemOfUnits => isMetric(systemOfUnits) ? Unit.METRIC : Unit.IMPERIAL;

/**
 * Ensures that `propertyA` comes before `propertyB` in the given array,
 * while keeping the order of all other elements unchanged.
 *
 * @param properties - Array of property names to reorder.
 * @param propertyA - The property that should come first.
 * @param propertyB - The property that should come after `propertyA`.
 * @returns A new array with `propertyA` positioned before `propertyB`.
 * 
 *  ```typescript
 * import {setPriorityOfProperties} from '@ion/dls/src/maps/helpers/MapViewUtils';
 * 
 * const properties = setPriorityOfProperties(properties, 'c', 'a');
 *
 * ...
 */
export const setPriorityOfProperties = (
  properties: string[],
  propertyA: string,
  propertyB: string
): string[] => {
  const first  = properties.indexOf(propertyA);
  const second = properties.indexOf(propertyB);
  // only adjust their relative position
  if (first > -1 && second > -1 && first > second) {
    properties.splice(second, 0, properties.splice(first, 1)[0]);
  }
  return properties;
};

/**
 * Creates an ArcGIS Extent that bounds all AVL events.
 * The extent is calculated by iterating through all event coordinates
 * and determining the minimum and maximum latitude/longitude values.
 * This bounding box can then be used to zoom the map view to include
 * all AVL points with optional padding.
 * 
 * ```typescript
 * import {createExtentFromAVL} from '@ion/dls/src/maps/helpers/MapViewUtils';
 *
 * const extent = createExtentFromAVL(avlEvents);
 * 
 * ...
 */
export const createExtentFromAVL = (events: any[]) => {
  if (events?.length) {
    let xmin = Infinity;
    let ymin = Infinity;
    let xmax = -Infinity;
    let ymax = -Infinity;

    events.forEach(e => {
      const x = e.longitude;
      const y = e.latitude;

      if (x < xmin) xmin = x;
      if (x > xmax) xmax = x;
      if (y < ymin) ymin = y;
      if (y > ymax) ymax = y;
    });

    return new Extent({
      xmin,
      ymin,
      xmax,
      ymax,
      spatialReference : {wkid: 4326}
    });
  }
  return null;
};
